import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE from "../../utils/api";

/*
  NascarContestSimulator.jsx

  Main flow:
  1) Select contest/settings
  2) Generate field
  3) Sim the field
  4) Select simmed lineups from Results
  5) Save selected results to Portfolio

  Modernized layout:
  - NFL-style Setup / Field / Results / Portfolio navigation
  - separate contest, field, and simulation cards with workflow sidebar
  - scoped responsive dark/light styles and entry-weighted field counts

  Key changes vs old flow:
  - portfolio is post-sim only
  - simulate_field payload does NOT send portfolio lineups
  - results table is selectable
  - tighter spreadsheet-style layout
  - added Drivers tab with field own / portfolio own / leverage / sim metrics
*/

/* ---------------- last updated ---------------- */
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch (_) {}

      try {
        const r = await fetch(mainUrl, { cache: "no-store" });
        const lm2 = r.headers.get("last-modified");
        if (alive && lm2) {
          setUpdatedAt(new Date(lm2));
          return;
        }
      } catch (_) {}

      try {
        if (!metaUrl) return;
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        }).then((x) => x.json());
        const iso =
          m?.updated_iso ||
          m?.updated_utc ||
          m?.updated ||
          m?.lastUpdated ||
          m?.timestamp;
        const ep = m?.updated_epoch;
        const d = iso
          ? new Date(iso)
          : Number.isFinite(ep)
            ? new Date(ep * 1000)
            : null;
        if (alive && d && !Number.isNaN(+d)) setUpdatedAt(d);
      } catch (_) {}
    })();

    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);

  return updatedAt;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

/* ---------------- helpers ---------------- */
const clamp = (v, lo = 0, hi = 1e9) => Math.max(lo, Math.min(hi, v));
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[$,% ]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const fmt0 = (n) => (Number.isFinite(n) ? Number(n).toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(n) ? Number(n).toFixed(1) : "—");
const fmt2 = (n) => (Number.isFinite(n) ? Number(n).toFixed(2) : "—");
const fmtPct = (n, d = 1) =>
  Number.isFinite(n) ? `${Number(n).toFixed(d)}%` : "—";
const fmtMoney = (n) =>
  Number.isFinite(n)
    ? Number(n).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : "—";

  function driverWithQual(driver, rowsByDriver) {
  const q = rowsByDriver?.get(driver)?.qual;
  return Number.isFinite(Number(q)) && Number(q) > 0
    ? `${driver} (${Number(q)})`
    : driver;
}

function lineupHasDriver(lineup, driverName) {
  const needle = normName(driverName);
  if (!needle) return true;

  return (lineup.drivers || []).some((d) => normName(d).includes(needle));
}

function lineupMatchesSelectedDrivers(lineup, selectedDrivers, matchMode = "any") {
  const filters = Array.isArray(selectedDrivers) ? selectedDrivers.filter(Boolean) : [];
  if (!filters.length) return true;

  if (matchMode === "all") {
    return filters.every((name) => lineupHasDriver(lineup, name));
  }

  return filters.some((name) => lineupHasDriver(lineup, name));
}

function isQuotaError(e) {
  const name = e?.name || "";
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

function uniqBy(arr, getKey) {
  const seen = new Set();
  const out = [];
  for (const item of arr || []) {
    const key = getKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

// Keep values tied to their storage key while switching series or sites.
// The previous site's state must never be written into the new site's key.
const useStickyState = (key, init) => {
  const getInit = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw);
    } catch (_) {}
    return typeof init === "function" ? init() : init;
  }, [key, init]);
  const [state, setState] = React.useState(() => ({key, value:getInit()}));
  const value = state.key === key ? state.value : getInit();
  React.useEffect(() => {
    if (state.key !== key) setState({key, value:getInit()});
  }, [key, state.key, getInit]);
  React.useEffect(() => {
    if (state.key !== key) return;
    try { localStorage.setItem(key, JSON.stringify(state.value)); } catch (_) {}
  }, [key, state]);
  const setValue = React.useCallback((next) => {
    setState(previous => {
      const current = previous.key === key ? previous.value : getInit();
      const updated = typeof next === "function" ? next(current) : next;
      return previous.key === key && Object.is(current, updated) ? previous : {key, value:updated};
    });
  }, [key, getInit]);
  return [value, setValue];
};

function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (!alive) return;
        setData(j);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e);
        setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err, loading };
}

const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};

const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z' -]/g, "")
    .replace(/\s+/g, " ")
    .trim();

function buildSiteIdIndex(siteIdsList) {
  const idx = new Map();

  for (const r of siteIdsList || []) {
    const id = String(
      r.id ??
        r.ID ??
        r.playerId ??
        r.player_id ??
        r.fd_id ??
        r.FD_ID ??
        r.dk_id ??
        r.DK_ID ??
        "",
    ).trim();
    if (!id) continue;

    const display = r.display != null ? String(r.display).trim() : "";
    const siteName = r.siteName != null ? String(r.siteName).trim() : "";
    const legacy =
      r.name ?? r.player ?? r.Player ?? r.displayName ?? r.Name ?? "";
    const legacyStr = legacy != null ? String(legacy).trim() : "";
    const aliases = Array.isArray(r.aliases) ? r.aliases.map(String) : [];
    const candidates = [display, siteName, legacyStr, ...aliases].filter(
      Boolean,
    );

    for (const nm of candidates) {
      const key = normName(nm);
      if (!key || idx.has(key)) continue;
      idx.set(key, { id, display, siteName, nameFromSite: nm });
    }
  }

  return idx;
}

function detectFdPrefix(siteIdsList) {
  const counts = new Map();
  for (const r of siteIdsList || []) {
    const px =
      r.slateId ??
      r.slate_id ??
      r.groupId ??
      r.group_id ??
      r.lid ??
      r.prefix ??
      r.fd_prefix ??
      null;
    if (px == null || px === "") continue;
    const key = String(px);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  if (counts.size === 1) return [...counts.keys()][0];
  if (counts.size > 1)
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  return null;
}

function downloadSiteLineupsCSV({
  lineups,
  site,
  rosterSize,
  siteIds,
  fname = "field_lineups_ids.csv",
}) {
  const siteKey = site === "fd" ? "fd" : "dk";
  const list = Array.isArray(siteIds?.[siteKey])
    ? siteIds[siteKey]
    : (siteIds?.sites?.[siteKey] ?? []);
  const idIndex = buildSiteIdIndex(list);
  const fdPrefix = siteKey === "fd" ? detectFdPrefix(list) : null;

  const header = Array.from({ length: rosterSize }, () => "D").join(",");

  const lines = (lineups || []).map((L) => {
    const names = Array.isArray(L.drivers) ? L.drivers : [];
    const cells = names.slice(0, rosterSize).map((name) => {
      const rec = idIndex.get(normName(name));
      if (!rec) return escapeCSV(name);

      if (siteKey === "fd") {
        const outId = /^[a-z]+-\d+$/i.test(rec.id)
          ? rec.id
          : fdPrefix
            ? `${fdPrefix}-${rec.id}`
            : rec.id;
        const idName = rec.siteName || rec.display || rec.nameFromSite || name;
        return escapeCSV(`${outId}:${idName}`);
      }

      const idName = rec.siteName || rec.display || rec.nameFromSite || name;
      return escapeCSV(`${idName} (${rec.id})`);
    });

    while (cells.length < rosterSize) cells.push("");
    return cells.join(",");
  });

  const csv = [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExposureCSV(rows, fname = "exposures.csv") {
  const header = "Driver,Count,Exposure %";
  const body = (rows || [])
    .map(
      (r) =>
        `${escapeCSV(r.driver)},${r.count},${Number(r.exposure ?? r.pct ?? 0).toFixed(1)}`,
    )
    .join("\n");
  const blob = new Blob([[header, body].filter(Boolean).join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadSimResultsCSV(rows, fname = "contest_sim_results.csv") {
  const header = [
    "Lineup",
    "Source",
    "Salary",
    "Projection",
    "ROI",
    "Win %",
    "Top 1 %",
    "Top 10 %",
    "Cash %",
    "Avg Finish",
    "Avg Dupes",
    "Avg Payout",
    "Drivers",
  ];

  const body = (rows || []).map((r, idx) =>
    [
      idx + 1,
      escapeCSV(r.source || "field"),
      r.salary ?? "",
      Number(r.total ?? r.projection ?? 0).toFixed(1),
      Number(r.roi ?? 0).toFixed(2),
      Number(r.winPct ?? 0).toFixed(2),
      Number(r.top1Pct ?? 0).toFixed(2),
      Number(r.top10Pct ?? 0).toFixed(2),
      Number(r.cashPct ?? 0).toFixed(2),
      Number(r.avgFinish ?? 0).toFixed(2),
      Number(r.dupesAvg ?? r.avgDupes ?? 0).toFixed(2),
      Number(r.payoutsAvg ?? r.avgPayout ?? 0).toFixed(2),
      escapeCSV((r.drivers || []).join(" • ")),
    ].join(","),
  );

  const csv = [header.join(","), ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadDriverStatsCSV(rows, fname = "driver_stats.csv") {
  const header = [
    "Driver",
    "Field Own %",
    "Portfolio Own %",
    "Leverage",
    "Avg ROI",
    "Cash %",
    "Win %",
    "Top 10 %",
    "Lineups",
  ];

  const body = (rows || []).map((r) =>
    [
      escapeCSV(r.driver),
      Number(r.fieldOwn || 0).toFixed(1),
      Number(r.portOwn || 0).toFixed(1),
      Number(r.leverage || 0).toFixed(1),
      Number(r.avgRoi || 0).toFixed(2),
      Number(r.cashPct || 0).toFixed(2),
      Number(r.winPct || 0).toFixed(2),
      Number(r.top10Pct || 0).toFixed(2),
      Number(r.lineups || 0),
    ].join(","),
  );

  const csv = [header.join(","), ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

function sortRows(rows, sortCfg, getterMap = {}) {
  const { key, dir } = sortCfg || {};
  const mult = dir === "asc" ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const ga = getterMap[key] ? getterMap[key](a) : a?.[key];
    const gb = getterMap[key] ? getterMap[key](b) : b?.[key];

    const aNum = typeof ga === "number" ? ga : Number(ga);
    const bNum = typeof gb === "number" ? gb : Number(gb);
    const bothNum = Number.isFinite(aNum) && Number.isFinite(bNum);

    if (bothNum) {
      if (aNum < bNum) return -1 * mult;
      if (aNum > bNum) return 1 * mult;
      return 0;
    }

    const as = String(ga ?? "");
    const bs = String(gb ?? "");
    return as.localeCompare(bs) * mult;
  });
}

function useTableSort(storageKey, defaultKey, defaultDir = "desc") {
  const [sortCfg, setSortCfg] = useStickyState(storageKey, {
    key: defaultKey,
    dir: defaultDir,
  });
  const onSort = (key) => {
    setSortCfg((prev) => ({
      key,
      dir: prev?.key === key ? (prev?.dir === "asc" ? "desc" : "asc") : "desc",
    }));
  };
  return [sortCfg, onSort];
}

/* ---------------- theme ---------------- */
const LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
  panel: "border border-slate-300 bg-white shadow-sm",
  section: "border border-slate-300 bg-white shadow-sm",
  input: "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400",
  tabsWrap: "border border-slate-300 bg-white",
  tabActive: "bg-sky-100 text-sky-900 border-sky-300",
  tabIdle: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
  toolbar: "border border-slate-300 bg-white",
  tableHead: "bg-slate-100 text-slate-900 border-b border-slate-300",
  rowOdd: "bg-white",
  rowEven: "bg-slate-50",
  rowHover: "hover:bg-sky-50",
  rowSelected: "bg-sky-100",
  soft: "bg-slate-50 border border-slate-200",
  metricLabel: "text-slate-500",
};

const DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
  panel: "border border-slate-800 bg-slate-900 shadow-sm",
  section: "border border-slate-800 bg-slate-900 shadow-sm",
  input:
    "bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500",
  tabsWrap: "border border-slate-800 bg-slate-900",
  tabActive: "bg-sky-500/20 text-sky-100 border-sky-500/40",
  tabIdle: "bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800",
  toolbar: "border border-slate-800 bg-slate-900",
  tableHead: "bg-slate-900 text-slate-100 border-b border-slate-700",
  rowOdd: "bg-slate-950",
  rowEven: "bg-slate-900/80",
  rowHover: "hover:bg-slate-800",
  rowSelected: "bg-sky-500/15",
  soft: "bg-slate-950 border border-slate-800",
  metricLabel: "text-slate-400",
};

/* ---------------- config ---------------- */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    roster: 6,
    cap: 50000,
    salary: "DK Sal",
    proj: "DK Proj",
    floor: "DK Floor",
    ceil: "DK Ceiling",
    pown: ["DK pOWN%", "DK pOWN"],
    opt: ["DK Opt%", "DK Opt"],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    roster: 5,
    cap: 50000,
    salary: "FD Sal",
    proj: "FD Proj",
    floor: "FD Floor",
    ceil: "FD Ceiling",
    pown: ["FD pOWN%", "FD pOWN"],
    opt: ["FD Opt%", "FD Opt"],
  },
};

const SERIES = {
  cup: {
    key: "cup",
    label: "Cup",
    projections: "/data/nascar/cup/latest/projections.json",
    siteIds: "/data/nascar/cup/latest/site_ids.json",
    contests: "/data/nascar/contests.json",
    route: "/nascar/cup/contest-simulator",
  },
  xfinity: {
    key: "xfinity",
    label: "O'Reilly",
    projections: "/data/nascar/xfinity/latest/projections.json",
    siteIds: "/data/nascar/xfinity/latest/site_ids.json",
    contests: "/data/nascar/contests.json",
    route: "/nascar/oreilly/contest-simulator",
  },
  trucks: {
    key: "trucks",
    label: "Trucks",
    projections: "/data/nascar/trucks/latest/projections.json",
    siteIds: "/data/nascar/trucks/latest/site_ids.json",
    contests: "/data/nascar/contests.json",
    route: "/nascar/trucks/contest-simulator",
  },
};

const PROFILE_OPTIONS = ["chalky", "balanced", "chaos"];
const FIELD_TYPES = ["sharp", "average", "soft"];
const CONTEST_BUCKETS = [
  "all",
  "large_field_gpp",
  "mid_field_gpp",
  "single_entry",
  "three_max",
  "mme",
  "cash",
  "custom",
];
const WORKSPACE_TABS = [
  "overview",
  "reference",
  "field",
  "results",
  "portfolio",
  "drivers",
];

const SERIES_GENERATE_PATHS = {
  cup: ["nascar/cup/generate_field", "cup/generate_field"],
  xfinity: ["nascar/xfinity/generate_field", "xfinity/generate_field"],
  trucks: ["nascar/trucks/generate_field", "trucks/generate_field"],
};

const SERIES_SIM_PATHS = {
  cup: ["nascar/cup/simulate_field", "cup/simulate_field"],
  xfinity: ["nascar/xfinity/simulate_field", "xfinity/simulate_field"],
  trucks: ["nascar/trucks/simulate_field", "trucks/simulate_field"],
};

const SERIES_GENERATE_STREAM_PATHS = {
  cup: [
    "nascar/cup/generate_field_stream",
    "cup/generate_field_stream",
    "generate_field_stream",
  ],
  xfinity: [
    "nascar/xfinity/generate_field_stream",
    "xfinity/generate_field_stream",
    "generate_field_stream",
  ],
  trucks: [
    "nascar/trucks/generate_field_stream",
    "trucks/generate_field_stream",
    "generate_field_stream",
  ],
};

const SERIES_SIM_STREAM_PATHS = {
  cup: [
    "nascar/cup/simulate_field_stream",
    "cup/simulate_field_stream",
    "simulate_field_stream",
  ],
  xfinity: [
    "nascar/xfinity/simulate_field_stream",
    "xfinity/simulate_field_stream",
    "simulate_field_stream",
  ],
  trucks: [
    "nascar/trucks/simulate_field_stream",
    "trucks/simulate_field_stream",
    "simulate_field_stream",
  ],
};

async function fetchFirstOk(paths, init) {
  for (const p of paths) {
    try {
      const base = API_BASE.replace(/\/$/, "");
      const res = await fetch(`${base}/${p}`, init);
      if (res.ok) return { res, path: p };
    } catch (_) {}
  }
  return null;
}

async function postSSE(url, payload, onEvent) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok || !res.body) {
    throw new Error(`Stream failed: HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() || "";

    for (const chunk of chunks) {
      const dataLine = chunk
        .split("\n")
        .find((line) => line.startsWith("data: "));

      if (!dataLine) continue;

      const ev = JSON.parse(dataLine.slice(6));
      onEvent?.(ev);

      if (ev.done) return ev;
    }
  }

  return null;
}

async function postSSEFirstOk(paths, payload, onEvent) {
  const base = API_BASE.replace(/\/$/, "");
  let lastError = null;

  for (const p of paths || []) {
    try {
      return await postSSE(`${base}/${p}`, payload, onEvent);
    } catch (e) {
      lastError = e;
    }
  }

  throw lastError || new Error("No working stream endpoint found.");
}

function stripStreamResult(ev) {
  if (!ev || typeof ev !== "object") return ev;
  const { result, ...progressOnly } = ev;
  return progressOnly;
}

/* ---------------- contest helpers ---------------- */
function classifyContest(c) {
  const name = String(c?.name || "").toLowerCase();
  const fee = num(c?.entryFee);
  const fieldSize = num(c?.fieldSize);
  const maxEntries = num(c?.maxEntriesPerUser);

  if (
    name.includes("double up") ||
    name.includes("50/50") ||
    name.includes("head-to-head") ||
    name.includes("h2h")
  )
    return "cash";
  if (maxEntries === 1 || name.includes("single entry")) return "single_entry";
  if (maxEntries === 3 || name.includes("3-max") || name.includes("three max"))
    return "three_max";
  if (maxEntries >= 20) return fieldSize >= 5000 ? "large_field_gpp" : "mme";
  if (fieldSize >= 5000 || fee <= 10) return "large_field_gpp";
  if (fieldSize >= 300) return "mid_field_gpp";
  return "mme";
}

function contestMatchesSeries(contest, seriesKey) {
  const name = String(contest?.name || "").toLowerCase();
  const sport = String(contest?.sportFromDetails || "").toLowerCase();

  if (seriesKey === "cup") {
    return (
      sport === "nas" &&
      !name.includes("(orly)") &&
      !name.includes("(xfinity)") &&
      !name.includes("(truck)") &&
      !name.includes("(trucks)")
    );
  }

  if (seriesKey === "xfinity") {
    return name.includes("(orly)") || name.includes("(xfinity)");
  }

  if (seriesKey === "trucks") {
    return name.includes("(truck)") || name.includes("(trucks)");
  }

  return true;
}

function normalizeContest(contest) {
  const payouts = Array.isArray(contest?.payouts)
    ? contest.payouts.map((p) => ({
        tierIndex: num(p.tierIndex),
        minPosition: num(p.minPosition),
        maxPosition: num(p.maxPosition),
        cashText: p.cashText ?? "",
        cashValue: num(p.cashValue),
        quantity: num(p.quantity),
        payoutDescriptionType: p.payoutDescriptionType ?? "",
      }))
    : [];

  return {
    ...contest,
    contestId: String(contest?.contestId ?? ""),
    entryFee: num(contest?.entryFee),
    fieldSize: num(contest?.fieldSize),
    entries: num(contest?.entries),
    prizePool: num(contest?.prizePool),
    topPrize: num(contest?.topPrize),
    maxEntriesPerUser: num(contest?.maxEntriesPerUser),
    fillPct: num(contest?.fillPct),
    payouts,
    bucket: classifyContest(contest),
  };
}

function getPct(obj, list) {
  if (!obj) return 0;
  const k = Array.isArray(list) ? list.find((x) => obj?.[x] != null) : list;
  if (!k) return 0;
  const v = num(obj[k]);
  return v > 0 ? v / 100 : 0;
}

function buildProjectionRows(data, cfg) {
  if (!data) return [];
  const arr = Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data)
      ? data
      : [];
  return arr
    .map((r) => {
      const driver = String(
        r?.Driver ?? r?.["Driver_1"] ?? r?.DRIVER ?? "",
      ).trim();
      const salary = num(r?.[cfg.salary]);
      const proj = num(r?.[cfg.proj]);
      return {
        driver,
        qual: num(r?.Qual ?? r?.QUAL),
        salary,
        proj,
        floor: num(r?.[cfg.floor]),
        ceil: num(r?.[cfg.ceil]),
        pown: getPct(r, cfg.pown),
        opt: getPct(r, cfg.opt),
        val: salary > 0 ? proj / (salary / 1000) : 0,
      };
    })
    .filter((r) => r.driver && r.salary > 0);
}

/* ---------------- small UI ---------------- */
function Section({ children, className = "", T }) {
  return (
    <section className={`ncl-section rounded-lg ${T.section} ${className}`}>
      {children}
    </section>
  );
}

function SectionHeader({ title, subtitle, right, eyebrow, compact = false, T }) {
  return (
    <div
      className={`ncl-section-header flex items-center justify-between gap-3 ${compact ? "px-3 py-2" : "px-3 py-3"} border-b ${T === DARK_THEME ? "border-slate-800" : "border-slate-200"}`}
    >
      <div>
        {eyebrow && <div className="ncl-eyebrow">{eyebrow}</div>}
        <div className="ncl-section-title text-base font-semibold leading-none">{title}</div>
        {subtitle ? (
          <div className={`mt-1 text-xs ${T.subtext}`}>{subtitle}</div>
        ) : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function MetricCell({ label, value, sub, T }) {
  return (
    <div
      className={`ncl-metric px-3 py-2 border-r border-b ${T === DARK_THEME ? "border-slate-800" : "border-slate-200"}`}
    >
      <div
        className={`text-[11px] uppercase tracking-[0.12em] ${T.metricLabel}`}
      >
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold leading-none">{value}</div>
      {sub ? (
        <div className={`mt-1 text-[11px] ${T.subtext}`}>{sub}</div>
      ) : null}
    </div>
  );
}

function Toolbar({ children, T }) {
  return <div className={`px-3 py-2 ${T.toolbar}`}>{children}</div>;
}

function Btn({ children, onClick, disabled, primary = false, small = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        `ncl-button ${primary ? "ncl-primary" : ""} ${small ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"} rounded border transition disabled:opacity-50 ` +
        (primary
          ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
          : "border-slate-400/50 hover:bg-slate-500/10")
      }
    >
      {children}
    </button>
  );
}

function Input({ className = "", T, ...props }) {
  return (
    <input
      {...props}
      className={`ncl-input w-full rounded border px-2 py-1.5 text-sm ${T.input} ${className}`}
    />
  );
}

function Select({ className = "", T, children, ...props }) {
  return (
    <select
      {...props}
      className={`ncl-input w-full rounded border px-2 py-1.5 text-sm ${T.input} ${className}`}
    >
      {children}
    </select>
  );
}

function TabButton({ active, onClick, children, T }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`ncl-tab ${active ? "is-active" : ""} rounded px-3 py-1.5 text-sm border ${active ? T.tabActive : T.tabIdle}`}
    >
      {children}
    </button>
  );
}

function SortableTh({
  label,
  sortKey,
  sortCfg,
  onSort,
  className = "",
  align = "center",
}) {
  const active = sortCfg?.key === sortKey;
  const arrow = active ? (sortCfg?.dir === "asc" ? " ▲" : " ▼") : "";
  const alignClass =
    align === "left"
      ? "text-left"
      : align === "right"
        ? "text-right"
        : "text-center";
  return (
    <th
      className={`px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none ${alignClass} ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <span className="opacity-60">{arrow}</span>
    </th>
  );
}

function SpreadsheetTable({ children, maxHeight = "520px", T }) {
  return (
    <div className="ncl-table-scroll overflow-auto" style={{ maxHeight }}>
      <table className="min-w-full text-[12px] border-collapse">
        {children}
      </table>
    </div>
  );
}

function DataRow({ selected, onClick, children, index, T }) {
  const base = index % 2 === 0 ? T.rowEven : T.rowOdd;
  return (
    <tr
      onClick={onClick}
      className={`${base} ${T.rowHover} ${selected ? T.rowSelected : ""} ${onClick ? "cursor-pointer" : ""}`}
    >
      {children}
    </tr>
  );
}

function RealProgressBar({ active, progress, title, T }) {
  if (!active && !progress) return null;

  const done = Number(progress?.doneCount ?? 0);
  const total = Number(progress?.total ?? 0);
  const hasPct = progress?.pct != null && Number.isFinite(Number(progress.pct));
  const determinate = hasPct || total > 0;
  const pct = hasPct
    ? Number(progress.pct)
    : total > 0
      ? Math.round((done / total) * 100)
      : 0;
  const safePct = Math.max(0, Math.min(100, pct || 0));

  return (
    <div className={`rounded-lg p-3 ${T.soft}`}>
      <div className="mb-2 flex items-center justify-between gap-3 text-xs">
        <div>
          <div className="font-semibold">{title}</div>
          <div className={`mt-1 ${T.subtext}`}>
            {progress?.message || "Working..."}
          </div>
        </div>

        <div className={`text-right ${T.subtext}`}>
          {total > 0 ? (
            <>
              <div>
                {fmt0(done)} / {fmt0(total)}
              </div>
              <div>{pct}%</div>
            </>
          ) : (
            <div>{determinate ? `${pct}%` : "Working…"}</div>
          )}
        </div>
      </div>

      <div className="h-2 w-full overflow-hidden rounded bg-slate-700/40" role="progressbar" aria-label={title} aria-valuemin={0} aria-valuemax={100} aria-valuenow={determinate ? safePct : undefined}>
        <div
          className={`h-full rounded bg-blue-500 transition-all duration-300 ${!determinate ? "ncl-indeterminate" : ""}`}
          style={{ width: determinate ? `${safePct}%` : "35%" }}
        />
      </div>
    </div>
  );
}

const MODERN_CSS = '.ncl{--bg:#0b1017;--panel:#141b24;--input:#0e151f;--line:#2a3545;--text:#edf2f8;--muted:#8e9fb4;--accent:#66b7ed;--tint:rgba(102,183,237,.12);background:var(--bg)!important;color:var(--text)!important;min-height:100vh;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;line-height:1.5;font-size:14px}\n.ncl-light{--bg:#f4f6fa;--panel:#fff;--input:#f8fafc;--line:#dce3ec;--text:#152238;--muted:#60738b;--accent:#1478b7;--tint:rgba(20,120,183,.08)}\n.ncl *{box-sizing:border-box}.ncl-shell{max-width:1600px!important;padding:32px 28px!important}.ncl-shell> *+*{margin-top:24px!important}.ncl h1{font-size:clamp(26px,3vw,38px);font-weight:750;letter-spacing:-.045em;line-height:1.15;margin:8px 0 12px}.ncl h3{font-size:16px;font-weight:650;margin:0 0 6px}.ncl p{margin-top:0}.ncl-eyebrow{font-size:10px;font-weight:750;letter-spacing:.17em;color:var(--accent);margin-bottom:8px}.ncl-help{color:var(--muted);font-size:12px;line-height:1.7;margin-bottom:0}.ncl-section{border:1px solid var(--line)!important;background:var(--panel)!important;border-radius:18px!important;overflow:hidden;box-shadow:none!important}.ncl-section-header{padding:24px!important;border-color:var(--line)!important;align-items:flex-start!important;flex-wrap:wrap}.ncl-section-title{font-size:20px!important;letter-spacing:-.025em;line-height:1.3!important;font-weight:700}.ncl-section-header .text-xs{font-size:12px;line-height:1.6;color:var(--muted);margin-top:8px}.ncl-card-body{padding:24px}.ncl-setup-layout{display:grid;grid-template-columns:minmax(0,1fr) 330px;gap:24px;align-items:start}.ncl-setup-main,.ncl-sidebar{display:grid;gap:24px;min-width:0}.ncl-sidebar{position:sticky;top:20px}.ncl-card-body> *+*{margin-top:20px}.ncl-card-body label{font-size:12px!important;font-weight:550!important;color:var(--muted);margin-bottom:8px}.ncl-card-body .grid{gap:20px}.ncl-input{border:1px solid var(--line)!important;background:var(--input)!important;color:var(--text)!important;border-radius:9px!important;min-height:40px;padding:9px 12px!important;font-size:13px!important;transition:border-color .15s;max-width:100%}.ncl-input:focus{outline:2px solid var(--accent);outline-offset:2px}.ncl-input::placeholder{color:var(--muted)}.ncl input[type=checkbox]{accent-color:var(--accent);width:16px;height:16px}.ncl-button{min-height:40px;border:1px solid var(--line)!important;border-radius:9px!important;padding:9px 15px!important;background:transparent;color:var(--text);font-size:12px!important;font-weight:650;cursor:pointer;transition:background .15s,border-color .15s}.ncl-button:hover:not(:disabled){background:var(--tint);border-color:var(--accent)!important}.ncl-primary{background:var(--accent)!important;border-color:var(--accent)!important;color:#0b2336!important}.ncl-light .ncl-primary{color:white!important}.ncl button:disabled{opacity:.45;cursor:not-allowed}.ncl button:focus-visible{outline:2px solid var(--accent);outline-offset:3px}.ncl-navigation{display:flex;gap:6px;overflow-x:auto;border-bottom:1px solid var(--line);padding-bottom:10px}.ncl-tab{display:inline-flex;align-items:center;gap:10px;white-space:nowrap;background:transparent!important;border:1px solid transparent!important;color:var(--muted)!important;padding:11px 16px!important;font-size:13px!important;border-radius:9px!important;font-weight:650;cursor:pointer}.ncl-tab:hover{background:var(--tint)!important;color:var(--text)!important}.ncl-tab.is-active{background:var(--tint)!important;border-color:var(--line)!important;color:var(--accent)!important}.ncl-tab-count{font-size:10px;border:1px solid var(--line);border-radius:6px;padding:1px 6px;font-variant-numeric:tabular-nums}.ncl-summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}.ncl-metric{padding:18px!important;border:1px solid var(--line)!important;border-radius:12px;background:var(--panel)!important;min-width:0}.ncl-summary-grid .ncl-metric{padding:22px!important}.ncl-metric .text-lg{font-size:28px!important;line-height:1.2!important;margin-top:12px!important;letter-spacing:-.035em}.ncl-metric .text-\\[11px\\]{color:var(--muted);font-size:11px}.ncl-summary-grid .ncl-metric>div:last-child{overflow-wrap:anywhere}.ncl-workflow{padding:12px 20px 20px;display:grid;gap:4px}.ncl-workflow-step{display:flex;align-items:flex-start;gap:12px;text-align:left;padding:14px 4px;background:transparent;border:none;color:var(--text);width:100%;cursor:pointer}.ncl-workflow-step:hover{color:var(--accent)}.ncl-step-number{display:grid;place-items:center;width:30px;height:30px;border:1px solid var(--line);border-radius:50%;flex-shrink:0;color:var(--muted);font-size:12px}.ncl-workflow-step.is-complete .ncl-step-number{color:var(--accent);border-color:var(--accent);background:var(--tint)}.ncl-workflow-step strong{font-size:12px;display:block}.ncl-workflow-step small{font-size:11px;display:block;color:var(--muted);margin-top:4px;overflow-wrap:anywhere}.ncl-snapshot{padding:8px 24px}.ncl-snapshot>div{display:flex;justify-content:space-between;gap:14px;padding:12px 0;border-bottom:1px solid var(--line);font-size:12px}.ncl-snapshot>div:last-child{border:0}.ncl-snapshot span{color:var(--muted)}.ncl-sidebar-help{padding:12px 24px 22px}.ncl-actions{display:flex;align-items:center;gap:10px;flex-wrap:wrap}.ncl-simulation-settings{display:grid;grid-template-columns:180px 1fr;gap:24px;align-items:center}.ncl-table-scroll{scrollbar-color:var(--line) transparent}.ncl table{width:100%;font-size:12px!important;border-collapse:collapse;color:var(--text)}.ncl thead{background:var(--input)!important;color:var(--muted)!important;position:sticky;top:0;z-index:1}.ncl th{font-size:11px;padding:14px 12px!important;white-space:nowrap;border-bottom:1px solid var(--line)}.ncl td{padding:14px 12px!important;border-bottom:1px solid var(--line);font-variant-numeric:tabular-nums}.ncl tbody tr{background:var(--panel)!important}.ncl tbody tr:hover{background:var(--tint)!important}.ncl tbody tr[class*="bg-sky"]{background:var(--tint)!important}.ncl td:first-child{font-weight:600}.ncl .text-slate-400,.ncl .text-slate-500,.ncl .text-slate-600{color:var(--muted)!important}.ncl .ncl-job-status{display:grid;gap:10px}.ncl-job-status>div{background:var(--panel)!important;border:1px solid var(--line)!important;border-radius:12px!important;padding:18px!important}.ncl-job-status .bg-blue-500{background:var(--accent)!important}.ncl .grid>.ncl-metric{border-radius:0}.ncl-summary-grid>.ncl-metric{border-radius:14px!important}.ncl-section .ncl-input.w-20{width:80px}.ncl-brand{max-width:650px}\n@media(max-width:1100px){.ncl-setup-layout{grid-template-columns:minmax(0,1fr) 280px}.ncl-shell{padding:24px 20px!important}.ncl-summary-grid{gap:10px}.ncl-summary-grid .ncl-metric{padding:16px!important}}\n@media(max-width:850px){.ncl-setup-layout{grid-template-columns:1fr}.ncl-sidebar{position:static;grid-template-columns:1fr 1fr}.ncl-summary-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.ncl-section-header{padding:20px!important}}\n@media(max-width:540px){.ncl-shell{padding:20px 12px!important}.ncl-shell> *+*{margin-top:18px!important}.ncl-sidebar{grid-template-columns:1fr}.ncl-card-body{padding:18px}.ncl-card-body .grid{grid-template-columns:minmax(0,1fr)!important}.ncl-card-body .col-span-2{grid-column:span 1!important}.ncl-simulation-settings{grid-template-columns:1fr}.ncl-metric .text-lg{font-size:24px!important}.ncl-tab{padding:10px 12px!important}}\n@media(prefers-reduced-motion:reduce){.ncl *{transition:none!important;animation:none!important}}\n';

/* ---------------- page ---------------- */
export default function NascarContestSimulator() {
  const location = useLocation();
  const navigate = useNavigate();

  const routeSeries = useMemo(() => {
    const p = (location?.pathname || "").toLowerCase();
    if (p.includes("/nascar/oreilly/") || p.includes("/nascar/xfinity/")) return "xfinity";
    if (p.includes("/nascar/trucks/")) return "trucks";
    if (p.includes("/nascar/cup/")) return "cup";
    const m = p.match(/\/nascar\/(cup|oreilly|xfinity|trucks)\b/);
    return m?.[1] === "oreilly" ? "xfinity" : m?.[1] || "cup";
  }, [location?.pathname]);

  const [series, setSeries] = useStickyState(
    "nascarContestSim.series",
    routeSeries || "cup",
  );
  useEffect(() => {
    if (routeSeries && routeSeries !== series) setSeries(routeSeries);
  }, [routeSeries, series, setSeries]);

  const seriesCfg = SERIES[series] || SERIES.cup;
  const projUrl = seriesCfg.projections;
  const idsUrl = seriesCfg.siteIds;
  const contestsUrl = seriesCfg.contests;

  const projMetaUrl = projUrl.replace(/projections\.json$/, "meta.json");
  const idsMetaUrl = idsUrl.replace(/site_ids\.json$/, "meta.json");
  const contestsMetaUrl = contestsUrl.replace(/contests\.json$/, "meta.json");

  const {
    data: projData,
    loading: projLoading,
    err: projErr,
  } = useJson(projUrl);
  const { data: siteIds, loading: idsLoading, err: idsErr } = useJson(idsUrl);
  const {
    data: contestsData,
    loading: contestsLoading,
    err: contestsErr,
  } = useJson(contestsUrl);

  const projUpdated = useLastUpdated(projUrl, projMetaUrl);
  const idsUpdated = useLastUpdated(idsUrl, idsMetaUrl);
  const contestsUpdated = useLastUpdated(contestsUrl, contestsMetaUrl);
  const updatedAt = useMemo(() => {
    const vals = [projUpdated, idsUpdated, contestsUpdated]
      .filter(Boolean)
      .map((d) => +d);
    return vals.length ? new Date(Math.max(...vals)) : null;
  }, [projUpdated, idsUpdated, contestsUpdated]);

  const keyPrefix = `nascarContestSim.${series}`;
  const [theme, setTheme] = useStickyState(`${keyPrefix}.theme`, "dark");
  const isDark = theme === "dark";
  const T = isDark ? DARK_THEME : LIGHT_THEME;

  const [site, setSite] = useStickyState(`${keyPrefix}.site`, "dk");
  const cfg = SITES[site];

  const rows = useMemo(
    () => buildProjectionRows(projData, cfg),
    [projData, cfg],
  );
  const rowsByDriver = useMemo(
    () => new Map(rows.map((r) => [r.driver, r])),
    [rows],
  );

  const [contestSearch, setContestSearch] = useState("");
  const [contestBucket, setContestBucket] = useStickyState(
    `${keyPrefix}.${site}.contestBucket`,
    "all",
  );
  const [selectedContestId, setSelectedContestId] = useStickyState(
    `${keyPrefix}.${site}.contestId`,
    "",
  );
  const forceCustomContest = site === "fd";
  const effectiveContestBucket = forceCustomContest ? "custom" : contestBucket;

  const [profile, setProfile] = useStickyState(
    `${keyPrefix}.${site}.profile`,
    "balanced",
  );
  const [fieldType, setFieldType] = useStickyState(
    `${keyPrefix}.${site}.fieldType`,
    "average",
  );
  const [fieldSizeOverride, setFieldSizeOverride] = useStickyState(
    `${keyPrefix}.${site}.fieldSizeOverride`,
    "",
  );

  const [minSalaryUsed, setMinSalaryUsed] = useStickyState(
    `${keyPrefix}.${site}.minSalaryUsed`,
    "",
  );

  const [randomnessPct, setRandomnessPct] = useStickyState(
    `${keyPrefix}.${site}.randomnessPct`,
    15,
  );
  const [offOptimalPct, setOffOptimalPct] = useStickyState(
    `${keyPrefix}.${site}.offOptimalPct`,
    22,
  );
  const [useDuplication, setUseDuplication] = useStickyState(
    `${keyPrefix}.${site}.useDuplication`,
    true,
  );
  const [sims, setSims] = useStickyState(`${keyPrefix}.${site}.sims`, 3000);

  const [customContestName, setCustomContestName] = useStickyState(
    `${keyPrefix}.${site}.customContestName`,
    "",
  );
  const [customFieldSize, setCustomFieldSize] = useStickyState(
    `${keyPrefix}.${site}.customFieldSize`,
    5000,
  );
  const [customEntryFee, setCustomEntryFee] = useStickyState(
    `${keyPrefix}.${site}.customEntryFee`,
    15,
  );
  const [customMaxEntries, setCustomMaxEntries] = useStickyState(
    `${keyPrefix}.${site}.customMaxEntries`,
    150,
  );
  const [customTopPrize, setCustomTopPrize] = useStickyState(
    `${keyPrefix}.${site}.customTopPrize`,
    10000,
  );
  const [customCashLine, setCustomCashLine] = useStickyState(
    `${keyPrefix}.${site}.customCashLine`,
    20,
  );

  const [fieldData, setFieldData] = useStickyState(
    `${keyPrefix}.${site}.fieldData`,
    null,
  );
  const [selectedFieldLineupKeys, setSelectedFieldLineupKeys] = useStickyState(
    `${keyPrefix}.${site}.selectedFieldLineupKeys`,
    [],
  );
  const [selectionMode, setSelectionMode] = useStickyState(
    `${keyPrefix}.${site}.selectionMode`,
    "manual",
  );
  const [selectFirstX, setSelectFirstX] = useStickyState(
    `${keyPrefix}.${site}.selectFirstX`,
    20,
  );

  const [resultData, setResultData] = useStickyState(
    `${keyPrefix}.${site}.resultData`,
    null,
  );
  const [selectedResultKeys, setSelectedResultKeys] = useStickyState(
    `${keyPrefix}.${site}.selectedResultKeys`,
    [],
  );

  const [resultSelectCount, setResultSelectCount] = useStickyState(
  `${keyPrefix}.${site}.resultSelectCount`,
  20,
);

const [resultDriverFilter, setResultDriverFilter] = useStickyState(
  `${keyPrefix}.${site}.resultDriverFilter`,
  "",
);

const [selectedResultDrivers, setSelectedResultDrivers] = useStickyState(
  `${keyPrefix}.${site}.selectedResultDrivers`,
  [],
);

const [resultDriverMode, setResultDriverMode] = useStickyState(
  `${keyPrefix}.${site}.resultDriverMode`,
  "all",
);

const [resultDriverMatch, setResultDriverMatch] = useStickyState(
  `${keyPrefix}.${site}.resultDriverMatch`,
  "any",
);

const [portfolio, setPortfolio] = useStickyState(
    `${keyPrefix}.${site}.portfolio`,
    [],
  );

  const [selectedExposureDriver, setSelectedExposureDriver] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [workspaceTab, setWorkspaceTab] = useStickyState(
    `${keyPrefix}.${site}.workspaceTab`,
    "overview",
  );
  const [playerPoolSearch, setPlayerPoolSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");

  const [isGenerating, setIsGenerating] = useState(false);
  const [isSimming, setIsSimming] = useState(false);
  const [genError, setGenError] = useState(null);
  const [simError, setSimError] = useState(null);
  const [genProgress, setGenProgress] = useState(null);
  const [simProgress, setSimProgress] = useState(null);

  const [contestSort, onContestSort] = useTableSort(
    `${keyPrefix}.${site}.contestSort`,
    "prizePool",
    "desc",
  );
  const [playerPoolSort, onPlayerPoolSort] = useTableSort(
    `${keyPrefix}.${site}.playerPoolSort`,
    "proj",
    "desc",
  );
  const [fieldPoolSort, onFieldPoolSort] = useTableSort(
    `${keyPrefix}.${site}.fieldPoolSort`,
    "total",
    "desc",
  );
  const [fieldExposureSort, onFieldExposureSort] = useTableSort(
    `${keyPrefix}.${site}.fieldExposureSort`,
    "exposure",
    "desc",
  );
  const [portfolioSort, onPortfolioSort] = useTableSort(
    `${keyPrefix}.${site}.portfolioSort`,
    "roi",
    "desc",
  );
  const [portfolioExposureSort, onPortfolioExposureSort] = useTableSort(
    `${keyPrefix}.${site}.portfolioExposureSort`,
    "exposure",
    "desc",
  );
  const [pairingSort, onPairingSort] = useTableSort(
    `${keyPrefix}.${site}.pairingSort`,
    "pct",
    "desc",
  );
  const [resultsSort, onResultsSort] = useTableSort(
    `${keyPrefix}.${site}.resultsSort`,
    "roi",
    "desc",
  );
  const [payoutSort, onPayoutSort] = useTableSort(
    `${keyPrefix}.${site}.payoutSort`,
    "tierIndex",
    "asc",
  );
  const [driverSort, onDriverSort] = useTableSort(
    `${keyPrefix}.${site}.driverSort`,
    "avgRoi",
    "desc",
  );

  useEffect(() => {
    if (site !== "fd") return;
    setCustomContestName((prev) => prev || "FanDuel Custom Contest");
    setCustomFieldSize((prev) => Number(prev) || 2353);
    setCustomEntryFee((prev) => Number(prev) || 9);
    setCustomMaxEntries((prev) => Number(prev) || 150);
    setCustomTopPrize((prev) => Number(prev) || 20000);
    setCustomCashLine((prev) => Number(prev) || 20);
  }, [
    site,
    setCustomContestName,
    setCustomFieldSize,
    setCustomEntryFee,
    setCustomMaxEntries,
    setCustomTopPrize,
    setCustomCashLine,
  ]);

  const normalizedContests = useMemo(() => {
    const arr = Array.isArray(contestsData?.contests)
      ? contestsData.contests
      : Array.isArray(contestsData)
        ? contestsData
        : [];
    return arr
      .filter((c) => contestMatchesSeries(c, series))
      .map(normalizeContest);
  }, [contestsData, series]);

  const filteredContests = useMemo(() => {
    const needle = contestSearch.trim().toLowerCase();
    const filtered = normalizedContests.filter((c) => {
      if (
        effectiveContestBucket !== "all" &&
        effectiveContestBucket !== "custom" &&
        c.bucket !== effectiveContestBucket
      )
        return false;
      if (!needle) return true;
      return (
        String(c.name || "")
          .toLowerCase()
          .includes(needle) ||
        String(c.entryFee || "").includes(needle) ||
        String(c.fieldSize || "").includes(needle) ||
        String(c.maxEntriesPerUser || "").includes(needle) ||
        String(c.prizePool || "").includes(needle)
      );
    });

    return sortRows(filtered, contestSort, { contest: (r) => r.name });
  }, [normalizedContests, effectiveContestBucket, contestSearch, contestSort]);

  const selectedContest = useMemo(() => {
    return (
      filteredContests.find(
        (c) => String(c.contestId) === String(selectedContestId),
      ) ||
      normalizedContests.find(
        (c) => String(c.contestId) === String(selectedContestId),
      ) ||
      null
    );
  }, [filteredContests, normalizedContests, selectedContestId]);

  useEffect(() => {
    if (effectiveContestBucket === "custom") {
      setSelectedContestId("");
      return;
    }
    if (
      selectedContestId &&
      filteredContests.some(
        (c) => String(c.contestId) === String(selectedContestId),
      )
    )
      return;
    setSelectedContestId(
      filteredContests.length ? String(filteredContests[0].contestId) : "",
    );
  }, [
    effectiveContestBucket,
    filteredContests,
    selectedContestId,
    setSelectedContestId,
  ]);

  const fieldLineups = useMemo(() => {
    const arr = Array.isArray(fieldData?.fieldLineups)
      ? fieldData.fieldLineups
      : Array.isArray(fieldData?.lineups)
        ? fieldData.lineups
        : [];

    return arr.map((L, idx) => ({
      ...L,
      source: L.source || "field",
      totalPown: Number(L.totalPown ?? 0),
      totalOpt: Number(L.totalOpt ?? 0),
      geoMeanPown: Number(L.geoMeanPown ?? 0),
      copies: Number(L.copies ?? 0) > 0
      ? Number(L.copies)
      : Math.max(1, Math.round(Number(L.dupes ?? L.avgDupes ?? 0)) + 1),

    dupes: Number(L.dupes ?? L.avgDupes ?? 0),
      _key: String(L.key || `${idx}::${(L.drivers || []).join("|")}`),
    }));
  }, [fieldData]);

  const selectedFieldLineups = useMemo(() => {
    if (selectionMode === "all") return fieldLineups;
    if (selectionMode === "first_x")
      return fieldLineups.slice(0, Math.max(0, Number(selectFirstX) || 0));
    const keySet = new Set(selectedFieldLineupKeys || []);
    return fieldLineups.filter((L) => keySet.has(L._key));
  }, [fieldLineups, selectedFieldLineupKeys, selectionMode, selectFirstX]);

  const resultLineupsBase = useMemo(() => {
    const arr = Array.isArray(resultData?.lineupResults)
      ? resultData.lineupResults
      : [];
    return arr.map((r, idx) => ({
      ...r,
      key: String(r.key || `${idx}::${(r.drivers || []).join("|")}`),
      source: r.source || "field",
      total: Number(r.total ?? r.projection ?? 0),
      salary: Number(r.salary ?? 0),
      roi: Number(r.roi ?? 0),
      winPct: Number(r.winPct ?? 0),
      top1Pct: Number(r.top1Pct ?? 0),
      top10Pct: Number(r.top10Pct ?? 0),
      cashPct: Number(r.cashPct ?? 0),
      avgFinish: Number(r.avgFinish ?? 0),
      dupesAvg: Number(r.dupesAvg ?? r.avgDupes ?? 0),
      payoutsAvg: Number(r.payoutsAvg ?? r.avgPayout ?? 0),
      totalPown: Number(r.totalPown ?? 0),
      totalOpt: Number(r.totalOpt ?? 0),
      geoMeanPown: Number(r.geoMeanPown ?? 0),
      dupes: Number(r.dupes ?? r.dupesAvg ?? 0),
    }));
  }, [resultData]);

  const resultDriverOptions = useMemo(() => {
  const needle = String(resultDriverFilter || "").trim().toLowerCase();
  const selected = new Set((selectedResultDrivers || []).map(normName));

  return rows
    .filter((r) => r.driver)
    .filter((r) => !selected.has(normName(r.driver)))
    .filter((r) => {
      if (!needle) return true;
      return r.driver.toLowerCase().includes(needle);
    })
    .sort((a, b) => {
      const aq = Number(a.qual || 999);
      const bq = Number(b.qual || 999);
      if (aq !== bq) return aq - bq;
      return String(a.driver).localeCompare(String(b.driver));
    })
    .slice(0, 12);
}, [rows, resultDriverFilter, selectedResultDrivers]);

const addResultDriverFilter = (driver) => {
  const d = String(driver || "").trim();
  if (!d) return;

  setSelectedResultDrivers((prev) => {
    const arr = Array.isArray(prev) ? prev : [];
    const exists = arr.some((x) => normName(x) === normName(d));
    return exists ? arr : [...arr, d];
  });

  setResultDriverFilter("");
};

const removeResultDriverFilter = (driver) => {
  setSelectedResultDrivers((prev) =>
    (Array.isArray(prev) ? prev : []).filter(
      (d) => normName(d) !== normName(driver),
    ),
  );
};

const clearResultDriverFilters = () => {
  setSelectedResultDrivers([]);
  setResultDriverFilter("");
};

const resultLineups = useMemo(() => {
  const sorted = sortRows(resultLineupsBase, resultsSort);
  const selected = Array.isArray(selectedResultDrivers)
    ? selectedResultDrivers.filter(Boolean)
    : [];

  if (!selected.length || resultDriverMode === "all") return sorted;

  return sorted.filter((r) => {
    const matches = lineupMatchesSelectedDrivers(
      r,
      selected,
      resultDriverMatch,
    );

    if (resultDriverMode === "include") return matches;
    if (resultDriverMode === "exclude") return !matches;

    return true;
  });
}, [
  resultLineupsBase,
  resultsSort,
  selectedResultDrivers,
  resultDriverMode,
  resultDriverMatch,
]);

  const selectedResultRows = useMemo(() => {
    const set = new Set(selectedResultKeys || []);
    return resultLineupsBase.filter((r) => set.has(r.key));
  }, [resultLineupsBase, selectedResultKeys]);

  const portfolioRows = useMemo(() => {
    const arr = Array.isArray(portfolio) ? portfolio : [];
    return arr.map((L, idx) => ({
      ...L,
      source: L.source || "portfolio",
      _portfolioKey: String(
        L._portfolioKey || `${idx}::${L.key || (L.drivers || []).join("|")}`,
      ),
    }));
  }, [portfolio]);

  const notes = useMemo(() => {
    const combined = [];
    if (Array.isArray(fieldData?.notes)) combined.push(...fieldData.notes);
    if (Array.isArray(resultData?.notes)) combined.push(...resultData.notes);
    return combined;
  }, [fieldData, resultData]);

  const fieldSummary = useMemo(() => {
    if (fieldData?.fieldSummary) return fieldData.fieldSummary;
    if (!fieldLineups.length) {
      return {
        lineupCount: 0,
        avgSalary: 0,
        avgProj: 0,
        avgLineupPown: 0,
        avgGeoMeanPown: 0,
        avgDupes: 0,
      };
    }
    const n = fieldLineups.length;
    return {
      lineupCount: n,
      avgSalary: fieldLineups.reduce((s, L) => s + (L.salary || 0), 0) / n,
      avgProj: fieldLineups.reduce((s, L) => s + (L.total || 0), 0) / n,
      avgLineupPown:
        fieldLineups.reduce((s, L) => s + (L.totalPown || 0), 0) / n,
      avgGeoMeanPown:
        fieldLineups.reduce((s, L) => s + (L.geoMeanPown || 0), 0) / n,
      avgDupes: fieldLineups.reduce((s, L) => s + (L.dupes || 0), 0) / n,
    };
  }, [fieldData, fieldLineups]);

  const portfolioSummary = useMemo(() => {
    if (!portfolioRows.length)
      return {
        count: 0,
        avgSalary: 0,
        avgProj: 0,
        avgPown: 0,
        avgGeo: 0,
        avgRoi: 0,
        cashPct: 0,
      };
    const n = portfolioRows.length;
    return {
      count: n,
      avgSalary: portfolioRows.reduce((s, L) => s + (L.salary || 0), 0) / n,
      avgProj: portfolioRows.reduce((s, L) => s + (L.total || 0), 0) / n,
      avgPown: portfolioRows.reduce((s, L) => s + (L.totalPown || 0), 0) / n,
      avgGeo: portfolioRows.reduce((s, L) => s + (L.geoMeanPown || 0), 0) / n,
      avgRoi: portfolioRows.reduce((s, L) => s + (L.roi || 0), 0) / n,
      cashPct: portfolioRows.reduce((s, L) => s + (L.cashPct || 0), 0) / n,
    };
  }, [portfolioRows]);

  const playerPoolRows = useMemo(() => {
    const needle = playerPoolSearch.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (!needle) return true;
      return (
        r.driver.toLowerCase().includes(needle) ||
        String(r.qual).includes(needle) ||
        String(r.salary).includes(needle)
      );
    });
    return sortRows(filtered, playerPoolSort, {
      pown: (r) => (r.pown || 0) * 100,
      opt: (r) => (r.opt || 0) * 100,
    });
  }, [rows, playerPoolSearch, playerPoolSort]);

  const sortedFieldLineups = useMemo(
    () => sortRows(fieldLineups, fieldPoolSort),
    [fieldLineups, fieldPoolSort],
  );
  const sortedPortfolioRows = useMemo(
    () => sortRows(portfolioRows, portfolioSort),
    [portfolioRows, portfolioSort],
  );

  const fieldExposureRowsBase = useMemo(() => {
    if (Array.isArray(fieldData?.fieldExposure)) return fieldData.fieldExposure;
    const m = new Map();
    const total = Math.max(1, fieldLineups.length);
    for (const L of fieldLineups) {
      for (const d of L.drivers || []) m.set(d, (m.get(d) || 0) + 1);
    }
    return [...m.entries()].map(([driver, count]) => ({
      driver,
      count,
      exposure: (count / total) * 100,
    }));
  }, [fieldData, fieldLineups]);

  const fieldExposureRows = useMemo(
    () => sortRows(fieldExposureRowsBase, fieldExposureSort),
    [fieldExposureRowsBase, fieldExposureSort],
  );

  const portfolioExposureRowsBase = useMemo(() => {
    const m = new Map();
    const total = Math.max(1, portfolioRows.length);
    for (const L of portfolioRows) {
      for (const d of L.drivers || []) m.set(d, (m.get(d) || 0) + 1);
    }
    return [...m.entries()].map(([driver, count]) => ({
      driver,
      count,
      exposure: (count / total) * 100,
    }));
  }, [portfolioRows]);

  const portfolioExposureRows = useMemo(
    () => sortRows(portfolioExposureRowsBase, portfolioExposureSort),
    [portfolioExposureRowsBase, portfolioExposureSort],
  );

  const selectedDriverPairingsBase = useMemo(() => {
    if (!selectedExposureDriver || !portfolioRows.length) return [];
    const counts = new Map();
    let totalLineups = 0;
    for (const L of portfolioRows) {
      const drivers = Array.isArray(L.drivers) ? L.drivers : [];
      if (!drivers.includes(selectedExposureDriver)) continue;
      totalLineups += 1;
      for (const d of drivers) {
        if (d === selectedExposureDriver) continue;
        counts.set(d, (counts.get(d) || 0) + 1);
      }
    }
    return [...counts.entries()].map(([driver, count]) => ({
      driver,
      count,
      pct: totalLineups ? (count / totalLineups) * 100 : 0,
    }));
  }, [selectedExposureDriver, portfolioRows]);

  const selectedDriverPairings = useMemo(
    () => sortRows(selectedDriverPairingsBase, pairingSort),
    [selectedDriverPairingsBase, pairingSort],
  );

  const driverStatsBase = useMemo(() => {
    const map = new Map();
    const ensure = (driver) => {
      if (!map.has(driver)) {
        const proj = rowsByDriver.get(driver);
        map.set(driver, {
          driver,
          qual: proj?.qual ?? 0,
          salary: proj?.salary ?? 0,
          proj: proj?.proj ?? 0,
          pown: (proj?.pown || 0) * 100,
          opt: (proj?.opt || 0) * 100,
          fieldCount: 0,
          portCount: 0,
          roiSum: 0,
          cashSum: 0,
          winSum: 0,
          top10Sum: 0,
          lineupCount: 0,
        });
      }
      return map.get(driver);
    };

    for (const r of resultLineupsBase) {
      for (const d of r.drivers || []) {
        const rec = ensure(d);
        rec.fieldCount += 1;
        rec.roiSum += Number(r.roi || 0);
        rec.cashSum += Number(r.cashPct || 0);
        rec.winSum += Number(r.winPct || 0);
        rec.top10Sum += Number(r.top10Pct || 0);
        rec.lineupCount += 1;
      }
    }

    for (const p of portfolioRows) {
      for (const d of p.drivers || []) {
        const rec = ensure(d);
        rec.portCount += 1;
      }
    }

    const totalField = Math.max(
      1,
      resultLineupsBase.length || fieldLineups.length || 1,
    );
    const totalPort = Math.max(1, portfolioRows.length || 1);

    return [...map.values()].map((r) => {
      const fieldOwn = (r.fieldCount / totalField) * 100;
      const portOwn = portfolioRows.length
        ? (r.portCount / totalPort) * 100
        : 0;
      return {
        driver: r.driver,
        qual: r.qual,
        salary: r.salary,
        proj: r.proj,
        pown: r.pown,
        opt: r.opt,
        fieldOwn,
        portOwn,
        leverage: portOwn - fieldOwn,
        avgRoi: r.lineupCount ? r.roiSum / r.lineupCount : 0,
        cashPct: r.lineupCount ? r.cashSum / r.lineupCount : 0,
        winPct: r.lineupCount ? r.winSum / r.lineupCount : 0,
        top10Pct: r.lineupCount ? r.top10Sum / r.lineupCount : 0,
        lineups: r.lineupCount,
      };
    });
  }, [resultLineupsBase, portfolioRows, rowsByDriver, fieldLineups.length]);

  const driverStatsRows = useMemo(() => {
    const needle = driverSearch.trim().toLowerCase();
    const filtered = driverStatsBase.filter(
      (r) => !needle || r.driver.toLowerCase().includes(needle),
    );
    return sortRows(filtered, driverSort);
  }, [driverStatsBase, driverSearch, driverSort]);

  function makeCustomContest() {
    const fieldSize = Math.max(2, Number(customFieldSize) || 2);
    const entryFee = Math.max(0, Number(customEntryFee) || 0);
    const maxEntries = Math.max(1, Number(customMaxEntries) || 1);
    const topPrize = Math.max(0, Number(customTopPrize) || 0);
    const cashLinePct = clamp(Number(customCashLine) || 20, 1, 100);
    const paidSpots = Math.max(1, Math.floor(fieldSize * (cashLinePct / 100)));

    const payouts = [];
    const totalPrizePool = fieldSize * entryFee;
    let remainingPrize = totalPrizePool;

    const winner = topPrize > 0 ? topPrize : Math.round(totalPrizePool * 0.2);
    remainingPrize -= winner;
    payouts.push({
      tierIndex: 1,
      minPosition: 1,
      maxPosition: 1,
      cashText: fmtMoney(winner),
      cashValue: winner,
      quantity: 1,
      payoutDescriptionType: "Text",
    });

    const preset = [0.12, 0.08, 0.06, 0.04, 0.03, 0.025, 0.02, 0.018, 0.016];
    let pos = 2;
    const paidTop = Math.min(10, paidSpots);

    for (let i = 0; i < paidTop - 1; i += 1) {
      const share = preset[i] || 0.015;
      const val = Math.max(entryFee * 1.5, Math.round(totalPrizePool * share));
      remainingPrize -= val;
      payouts.push({
        tierIndex: i + 2,
        minPosition: pos,
        maxPosition: pos,
        cashText: fmtMoney(val),
        cashValue: val,
        quantity: 1,
        payoutDescriptionType: "Text",
      });
      pos += 1;
    }

    if (pos <= paidSpots) {
      const val = Math.max(
        entryFee * 1.5,
        Math.floor(Math.max(0, remainingPrize) / (paidSpots - pos + 1)),
      );
      payouts.push({
        tierIndex: payouts.length + 1,
        minPosition: pos,
        maxPosition: paidSpots,
        cashText: fmtMoney(val),
        cashValue: val,
        quantity: 1,
        payoutDescriptionType: "Text",
      });
    }

    return {
      contestId: "custom",
      name: customContestName || `Custom ${seriesCfg.label} Contest`,
      entryFee,
      fieldSize,
      entries: fieldSize,
      prizePool: totalPrizePool,
      topPrize: winner,
      maxEntriesPerUser: maxEntries,
      fillPct: 100,
      payouts,
      bucket: "custom",
      contestSummary: `Custom ${fieldSize}-entry contest at ${fmtMoney(entryFee)} with ${fmtMoney(totalPrizePool)} in total prizes.`,
    };
  }

  const sortedPayouts = useMemo(() => {
    const payouts =
      (effectiveContestBucket === "custom"
        ? makeCustomContest()
        : selectedContest
      )?.payouts || [];
    return sortRows(payouts, payoutSort);
  }, [
    effectiveContestBucket,
    selectedContest,
    payoutSort,
    customContestName,
    customFieldSize,
    customEntryFee,
    customMaxEntries,
    customTopPrize,
    customCashLine,
  ]);

  async function generateField() {
    if (isGenerating || isSimming) return;
    const chosenContest =
      effectiveContestBucket === "custom"
        ? makeCustomContest()
        : selectedContest;
    if (!chosenContest) {
      setGenError("Select a contest or switch to custom.");
      return;
    }

    setFieldData(null);
    setIsGenerating(true);
    setGenError(null);
    setSimError(null);
    setSelectedFieldLineupKeys([]);
    setSelectedResultKeys([]);
    setPortfolio([]);
    setResultData(null);
    setSelectedExposureDriver(null);
    setSimProgress(null);
    setGenProgress({
      stage: "starting",
      doneCount: 0,
      total:
        Number(fieldSizeOverride) ||
        Number(chosenContest?.fieldSize) ||
        Number(chosenContest?.entries) ||
        0,
      pct: 0,
      message: "Starting field generation...",
    });

    const payload = {
      series,
      site,
      contest: effectiveContestBucket === "custom" ? null : chosenContest,
      customContest: effectiveContestBucket === "custom" ? chosenContest : null,
      fieldSettings: {
        profile,
        fieldType,
        randomnessPct: clamp(Number(randomnessPct) || 0, 0, 100),
        offOptimalPct: clamp(Number(offOptimalPct) || 0, 0, 100),
        useDuplication: !!useDuplication,
        fieldSizeOverride:
          fieldSizeOverride === ""
            ? null
            : Math.max(2, Number(fieldSizeOverride) || 2),

        minSalaryUsed:
          minSalaryUsed === ""
            ? null
            : Math.max(0, Number(minSalaryUsed) || 0),
      },
      projections: rows,
      siteIds,
    };

    try {
      const finalEvent = await postSSEFirstOk(
        SERIES_GENERATE_STREAM_PATHS[series] || SERIES_GENERATE_STREAM_PATHS.cup,
        payload,
        (ev) => {
          setGenProgress({...stripStreamResult(ev), message:ev.message || (ev.done ? "Your field is ready to simulate." : "Building the field…")});

          if (ev.error) {
            throw new Error(ev.error);
          }

          if (ev.result) {
            setFieldData(ev.result);
            setWorkspaceTab("field");
          }
        },
      );

      if (finalEvent?.error) {
        throw new Error(finalEvent.error);
      }

      if (finalEvent?.result) {
        setFieldData(finalEvent.result);
        setWorkspaceTab("field");
      }
    } catch (e) {
      setGenError(String(e?.message || e || "Field generation failed."));
    } finally {
      setIsGenerating(false);
    }
  }

  async function simulateField() {
    if (isGenerating || isSimming) return;
    if (!fieldLineups.length) {
      setSimError("Generate the field first.");
      return;
    }

    const chosenContest =
      effectiveContestBucket === "custom"
        ? makeCustomContest()
        : selectedContest;
    if (!chosenContest) {
      setSimError("Select a contest or switch to custom.");
      return;
    }

    setResultData(null);
    setIsSimming(true);
    setSimError(null);
    setSelectedResultKeys([]);
    setPortfolio([]);
    setSelectedExposureDriver(null);
    setGenProgress(null);
    setSimProgress({
      stage: "starting",
      doneCount: 0,
      total: Math.max(100, Number(sims) || 100),
      pct: 0,
      message: "Starting simulations...",
    });

    const payload = {
      series,
      site,
      contest: effectiveContestBucket === "custom" ? null : chosenContest,
      customContest: effectiveContestBucket === "custom" ? chosenContest : null,
      sims: Math.max(100, Number(sims) || 100),
      fieldLineups: fieldLineups.map((L) => {
        const copies =
          Number(L.copies) > 0
            ? Number(L.copies)
            : Math.max(1, Math.round(Number(L.dupes || 0)) + 1);

        return {
          key: L._key,
          drivers: L.drivers || [],
          salary: Number(L.salary) || 0,
          total: Number(L.total) || 0,
          totalPown: Number(L.totalPown) || 0,
          totalOpt: Number(L.totalOpt) || 0,
          geoMeanPown: Number(L.geoMeanPown) || 0,
          copies,
          dupes: Math.max(0, copies - 1),
          source: "field",
        };
      }),
      projections: rows,
    };

    try {
      const finalEvent = await postSSEFirstOk(
        SERIES_SIM_STREAM_PATHS[series] || SERIES_SIM_STREAM_PATHS.cup,
        payload,
        (ev) => {
          setSimProgress({...stripStreamResult(ev), message:ev.message || (ev.done ? "Results are ready to review." : "Simulating race outcomes…")});

          if (ev.error) {
            throw new Error(ev.error);
          }

          if (ev.result) {
            setResultData(ev.result);
            setWorkspaceTab("results");
          }
        },
      );

      if (finalEvent?.error) {
        throw new Error(finalEvent.error);
      }

      if (finalEvent?.result) {
        setResultData(finalEvent.result);
        setWorkspaceTab("results");
      }
    } catch (e) {
      setSimError(String(e?.message || e || "Simulation failed."));
    } finally {
      setIsSimming(false);
    }
  }

  const addSelectedResultsToPortfolio = () => {
    if (!selectedResultRows.length) return;
    setPortfolio((prev) => {
      const next = [
        ...(Array.isArray(prev) ? prev : []),
        ...selectedResultRows.map((L) => ({ ...L, source: "portfolio" })),
      ];
      return uniqBy(
        next,
        (r) => r.key || `${(r.drivers || []).join("|")}::${r.salary}`,
      );
    });
    setWorkspaceTab("portfolio");
  };

  const replacePortfolioFromResults = () => {
    setPortfolio(
      selectedResultRows.map((L) => ({ ...L, source: "portfolio" })),
    );
    setSelectedExposureDriver(null);
    setWorkspaceTab("portfolio");
  };

  const clearPortfolio = () => {
    setPortfolio([]);
    setSelectedExposureDriver(null);
  };

  const selectAllFieldLineups = () => {
    setSelectionMode("manual");
    setSelectedFieldLineupKeys(fieldLineups.map((L) => L._key));
  };

  const clearSelectedFieldLineups = () => {
    setSelectionMode("manual");
    setSelectedFieldLineupKeys([]);
  };

const selectAllResultLineups = () =>
  setSelectedResultKeys(resultLineups.map((r) => r.key));

const selectTopXResultLineups = () => {
  const count = Math.max(0, Number(resultSelectCount) || 0);
  setSelectedResultKeys(resultLineups.slice(0, count).map((r) => r.key));
};

const addTopXResultLineups = () => {
  const count = Math.max(0, Number(resultSelectCount) || 0);
  const topKeys = resultLineups.slice(0, count).map((r) => r.key);

  setSelectedResultKeys((prev) => {
    const set = new Set(prev || []);
    topKeys.forEach((k) => set.add(k));
    return [...set];
  });
};

const clearSelectedResultLineups = () => setSelectedResultKeys([]);

  const removePortfolioLineup = (idx) => {
    setPortfolio((prev) =>
      Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : prev,
    );
  };

  const activeContest = effectiveContestBucket === "custom" ? makeCustomContest() : selectedContest;
  const fieldEntryCount = fieldLineups.reduce((total, lineup) => total + Math.max(1, Number(lineup.copies) || 1), 0);
  function clearWorkspace() {
    setFieldData(null); setResultData(null); setPortfolio([]);
    setSelectedFieldLineupKeys([]); setSelectedResultKeys([]);
    setSelectedExposureDriver(null); setGenProgress(null); setSimProgress(null);
    setGenError(null); setSimError(null);
  }

  const showErrorBanner =
    genError || simError || projErr || idsErr || contestsErr;

  return (
    <div className={`ncl ${isDark ? "ncl-dark" : "ncl-light"} ${T.page} pb-10`}>
      <style>{'.ncl-lineup-column,.ncl-lineup-cell{min-width:250px;max-width:340px}.ncl-driver-chips{display:grid;grid-template-columns:repeat(2,minmax(100px,1fr));gap:6px}.ncl-driver-chips span{background:var(--input);border:1px solid var(--line);padding:5px 8px;border-radius:6px;font-size:11px;white-space:normal!important;line-height:1.4}.ncl-table-scroll td.ncl-lineup-cell{padding:12px!important}' + MODERN_CSS + '\n.ncl fieldset{margin:0;border:0;min-width:0}.ncl-switch{border-radius:9px!important;min-height:38px;padding:9px 14px!important;font-weight:600}.ncl-empty{text-align:center;padding:70px 24px}.ncl-empty h2{font-size:26px;font-weight:700;letter-spacing:-.03em;margin:8px 0 12px}.ncl-empty p{color:var(--muted);max-width:480px;margin:0 auto 24px;font-size:13px}.ncl-indeterminate{animation:ncl-loading 1.3s ease-in-out infinite}@keyframes ncl-loading{from{transform:translateX(-100%)}to{transform:translateX(300%)}}\n'}</style>
      <div className="ncl-shell mx-auto w-full max-w-[1800px] px-4 py-4 space-y-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="ncl-brand"><div className="ncl-eyebrow">NASCAR CONTEST LAB</div>
            <h1 className={`text-3xl font-bold tracking-tight ${T.headerText}`}>
              Build your race-day field
            </h1><p className="ncl-help">{seriesCfg.label} · {SITES[site].label} · Generate, simulate, and build your portfolio.</p></div>
            {updatedAt ? (
              <div className={`text-sm ${T.subtext}`}>
                Updated: {fmtUpdated(updatedAt)}
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={`inline-flex items-center gap-1 rounded p-1 ${T.tabsWrap}`}
            >
              {Object.keys(SERIES).map((k) => {
                const active = series === k;
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={isGenerating || isSimming}
                    onClick={() => {
                      setSeries(k);
                      const to =
                        SERIES[k]?.route || `/nascar/${k}/contest-simulator`;
                      if ((location?.pathname || "") !== to) navigate(to);
                    }}
                    className={`ncl-switch rounded px-3 py-1.5 text-xs uppercase tracking-[0.12em] border ${active ? T.tabActive : T.tabIdle}`}
                  >
                    {SERIES[k].label}
                  </button>
                );
              })}
            </div>

            <div
              className={`inline-flex items-center gap-1 rounded p-1 ${T.tabsWrap}`}
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={`rounded px-3 py-1.5 text-sm border ${isDark ? T.tabActive : T.tabIdle}`}
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={`rounded px-3 py-1.5 text-sm border ${!isDark ? T.tabActive : T.tabIdle}`}
              >
                Light
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {Object.keys(SITES).map((s) => {
            const active = site === s;
            return (
              <button
                key={s}
                type="button"
                disabled={isGenerating || isSimming}
                onClick={() => setSite(s)}
                className={`ncl-switch inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm border ${active ? T.tabActive : T.tabIdle}`}
              >
                <img src={SITES[s].logo} alt="" className="w-4 h-4" />
                <span>{SITES[s].label}</span>
              </button>
            );
          })}
        </div>

        <div className="ncl-summary-grid">
          <MetricCell T={T} label="Contest entries" value={fmt0(fieldSizeOverride || activeContest?.fieldSize || 0)} sub={activeContest ? `${fmtMoney(activeContest.entryFee)} per entry` : "Select a contest"} />
          <MetricCell T={T} label="Prize pool" value={fmtMoney(activeContest?.prizePool || 0)} sub={activeContest?.name || "Configure below"} />
          <MetricCell T={T} label="Unique lineups" value={fmt0(fieldLineups.length)} sub={`${fmt0(fieldEntryCount)} total field entries`} />
          <MetricCell T={T} label="Simulation runs" value={fmt0(sims)} sub={resultLineups.length ? `${fmt0(resultLineups.length)} lineups evaluated` : "Ready when your field is built"} />
        </div>
        <div className="ncl-navigation">
          {WORKSPACE_TABS.map((tab) => (
            <TabButton
              key={tab}
              active={workspaceTab === tab}
              onClick={() => setWorkspaceTab(tab)}
              T={T}
            >
              {{overview:"Setup", field:"Field", results:"Results", portfolio:"Portfolio", drivers:"Drivers", reference:"Player Pool & Payouts"}[tab]}
              {["field","results","portfolio"].includes(tab) && <span className="ncl-tab-count">{fmt0(tab === "field" ? fieldLineups.length : tab === "results" ? resultLineups.length : portfolioRows.length)}</span>}
            </TabButton>
          ))}
        </div>

        {(isGenerating || isSimming || genProgress || simProgress) && <div className="ncl-job-status" role="status" aria-live="polite">
          <RealProgressBar active={isGenerating} progress={genProgress} title={isGenerating ? "Generating field" : "Field ready"} T={T} />
          <RealProgressBar active={isSimming} progress={simProgress} title={isSimming ? "Simulating contest" : "Simulation complete"} T={T} />
        </div>}
        {workspaceTab === "overview" && (
          <div className="ncl-setup-layout">
            <div className="ncl-setup-main">
              <Section T={T} className="ncl-setup-card">
                <SectionHeader T={T} eyebrow="01 / CONTEST" title="Make it your contest" subtitle="Choose an available contest or create your own size and payout structure." />
                <fieldset className="ncl-card-body" disabled={isGenerating || isSimming}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                        Contest Type
                      </label>
                      <Select
                        T={T}
                        value={effectiveContestBucket}
                        onChange={(e) => {
                          if (forceCustomContest) return;
                          setContestBucket(e.target.value);
                        }}
                        disabled={forceCustomContest}
                      >
                        {CONTEST_BUCKETS.map((v) => (
                          <option key={v} value={v}>
                            {v.replaceAll("_", " ")}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                        Search
                      </label>
                      <Input
                        T={T}
                        aria-label={"contest Search"}
                          value={contestSearch}
                        onChange={(e) => setContestSearch(e.target.value)}
                        placeholder="marquee, single entry, $12"
                      />
                    </div>
                  </div>

                  {effectiveContestBucket !== "custom" ? (
                    <SpreadsheetTable T={T} maxHeight="280px">
                      <thead className={T.tableHead}>
                        <tr>
                          <th className="px-2 py-2"></th>
                          <SortableTh
                            label="Contest"
                            sortKey="name"
                            sortCfg={contestSort}
                            onSort={onContestSort}
                            align="left"
                          />
                          <SortableTh
                            label="Pool"
                            sortKey="prizePool"
                            sortCfg={contestSort}
                            onSort={onContestSort}
                          />
                          <SortableTh
                            label="Fee"
                            sortKey="entryFee"
                            sortCfg={contestSort}
                            onSort={onContestSort}
                          />
                          <SortableTh
                            label="Field"
                            sortKey="fieldSize"
                            sortCfg={contestSort}
                            onSort={onContestSort}
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContests.map((c, idx) => {
                          const active =
                            String(selectedContestId) === String(c.contestId);
                          return (
                            <DataRow
                              key={c.contestId}
                              index={idx}
                              selected={active}
                              onClick={() =>
                                setSelectedContestId(String(c.contestId))
                              }
                              T={T}
                            >
                              <td className="px-2 py-2 text-center">
                                <input type="radio" checked={active} readOnly />
                              </td>
                              <td className="px-2 py-2 text-left">
                                <div className="font-medium">{c.name}</div>
                                <div className={`text-[11px] ${T.subtext}`}>
                                  {c.contestSummary}
                                </div>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {fmtMoney(c.prizePool)}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {fmtMoney(c.entryFee)}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {fmt0(c.fieldSize)}
                              </td>
                            </DataRow>
                          );
                        })}
                        {!filteredContests.length ? (
                          <tr>
                            <td
                              colSpan={5}
                              className={`px-3 py-8 text-center ${T.subtext}`}
                            >
                              No contests match these filters.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </SpreadsheetTable>
                  ) : (
                    <>
                      {forceCustomContest ? (
                        <div
                          className={`mb-3 rounded border px-3 py-2 text-sm ${
                            isDark
                              ? "border-amber-700/40 bg-amber-950/30 text-amber-200"
                              : "border-amber-300 bg-amber-50 text-amber-800"
                          }`}
                        >
                          FanDuel is currently using custom contest setup only.
                        </div>
                      ) : null}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label
                            className={`block text-[11px] mb-1 ${T.subtext}`}
                          >
                            Custom Name
                          </label>
                          <Input
                            T={T}
                            aria-label={"custom Contest Name"}
                          value={customContestName}
                            onChange={(e) =>
                              setCustomContestName(e.target.value)
                            }
                            placeholder="Marquee clone"
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-[11px] mb-1 ${T.subtext}`}
                          >
                            Field Size
                          </label>
                          <Input
                            T={T}
                            aria-label={"custom Field Size"}
                          value={customFieldSize}
                            onChange={(e) => setCustomFieldSize(e.target.value)}
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-[11px] mb-1 ${T.subtext}`}
                          >
                            Entry Fee
                          </label>
                          <Input
                            T={T}
                            aria-label={"custom Entry Fee"}
                          value={customEntryFee}
                            onChange={(e) => setCustomEntryFee(e.target.value)}
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-[11px] mb-1 ${T.subtext}`}
                          >
                            Max Entries/User
                          </label>
                          <Input
                            T={T}
                            aria-label={"custom Max Entries"}
                          value={customMaxEntries}
                            onChange={(e) =>
                              setCustomMaxEntries(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-[11px] mb-1 ${T.subtext}`}
                          >
                            Top Prize
                          </label>
                          <Input
                            T={T}
                            aria-label={"custom Top Prize"}
                          value={customTopPrize}
                            onChange={(e) => setCustomTopPrize(e.target.value)}
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-[11px] mb-1 ${T.subtext}`}
                          >
                            Cash Line %
                          </label>
                          <Input
                            T={T}
                            aria-label={"custom Cash Line"}
                          value={customCashLine}
                            onChange={(e) => setCustomCashLine(e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <Btn small onClick={() => setWorkspaceTab("reference")}>Review payouts</Btn>
                </fieldset>
              </Section>
              <Section T={T} className="ncl-setup-card">
                <SectionHeader T={T} eyebrow="02 / FIELD" title="Shape the competition" subtitle="Set the field's strength, ownership profile, and lineup variety." />
                <fieldset className="ncl-card-body" disabled={isGenerating || isSimming}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                        Profile
                      </label>
                      <Select
                        T={T}
                        value={profile}
                        onChange={(e) => setProfile(e.target.value)}
                      >
                        {PROFILE_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                        Field strength
                      </label>
                      <Select
                        T={T}
                        value={fieldType}
                        onChange={(e) => setFieldType(e.target.value)}
                      >
                        {FIELD_TYPES.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </Select>
                    </div>
                      <div>
                        <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                          Field Size Override
                        </label>
                        <Input
                          T={T}
                          aria-label={"field Size Override"}
                          value={fieldSizeOverride}
                          onChange={(e) => setFieldSizeOverride(e.target.value)}
                          placeholder="blank = contest size"
                        />
                      </div>

                      <div>
                        <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                          Minimum salary used ($)
                        </label>
                        <Input
                          T={T}
                          aria-label={"min Salary Used"}
                          value={minSalaryUsed}
                          onChange={(e) => setMinSalaryUsed(e.target.value)}
                          placeholder={site === "dk" ? "ex: 49000" : "ex: 48000"}
                        />
                      </div>

                      <div>
                        <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                          Randomness (%)
                        </label>
                        <Input
                          T={T}
                          aria-label={"randomness Pct"}
                          value={randomnessPct}
                          onChange={(e) => setRandomnessPct(e.target.value)}
                        />
                      </div>
                    <div>
                      <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                        Allowed off-optimal (%)
                      </label>
                      <Input
                        T={T}
                        aria-label={"off Optimal Pct"}
                          value={offOptimalPct}
                        onChange={(e) => setOffOptimalPct(e.target.value)}
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-2 pt-1">
                      <input
                        id="dupbox"
                        type="checkbox"
                        checked={!!useDuplication}
                        onChange={(e) => setUseDuplication(e.target.checked)}
                      />
                      <label htmlFor="dupbox" className="text-sm">
                        Allow duplicate lineups
                      </label>
                    </div>
                  </div>


                  <p className="ncl-help">Randomness varies candidate lineups. Allowed off-optimal controls how far their projections may fall below the best lineup. A 0% limit can sharply reduce variety.</p>
                </fieldset>
              </Section>
              <Section T={T} className="ncl-setup-card">
                <SectionHeader T={T} eyebrow="03 / SIMULATIONS" title="Run the contest" subtitle="Generate the competition, then evaluate it against your selected payouts." />
                <fieldset className="ncl-card-body" disabled={isGenerating || isSimming}>
                  <div className="ncl-simulation-settings">
                    <div>
                      <label className={`block text-[11px] mb-1 ${T.subtext}`}>
                        Simulation runs
                      </label>
                      <Input
                        T={T}
                        aria-label={"sims"}
                          value={sims}
                        onChange={(e) => setSims(e.target.value)}
                      />
                    </div>

                    <div><h3>Every lineup. Every race outcome.</h3><p className="ncl-help">Compare ROI, win rate, and cash rate. Save selected results to build your portfolio.</p></div>
                  </div>
                  <div className="ncl-actions">
                    <Btn primary onClick={generateField} disabled={isGenerating || isSimming || projLoading || idsLoading || !rows.length}>{isGenerating ? "Generating…" : fieldLineups.length ? "Regenerate field" : "Generate field"}</Btn>
                    <Btn onClick={simulateField} disabled={isGenerating || isSimming || !fieldLineups.length}>{isSimming ? "Running…" : "Run simulation"}</Btn>
                    <Btn small onClick={clearWorkspace} disabled={isGenerating || isSimming}>Clear workspace</Btn>
                  </div>
                </fieldset>
              </Section>
            </div>
            <aside className="ncl-sidebar">
              <Section T={T}>
                <SectionHeader T={T} eyebrow="WORKFLOW" title="Your game plan" />
                <div className="ncl-workflow">
                  {[
                    ["Configure your contest", activeContest ? activeContest.name : "Choose a contest to begin", !!activeContest, "overview"],
                    ["Generate the field", fieldLineups.length ? `${fmt0(fieldEntryCount)} entries · ${fmt0(fieldLineups.length)} unique` : "Build your competition", !!fieldLineups.length, "field"],
                    ["Simulate the contest", resultLineups.length ? `${fmt0(resultLineups.length)} lineups evaluated` : "Compare outcomes and payouts", !!resultLineups.length, "results"],
                    ["Build your portfolio", portfolioRows.length ? `${fmt0(portfolioRows.length)} lineups saved` : "Save and export selected results", !!portfolioRows.length, "portfolio"]
                  ].map(([title, detail, complete, tab], index) => <button type="button" key={title} className={`ncl-workflow-step ${complete ? "is-complete" : ""}`} onClick={() => setWorkspaceTab(tab)}><span className="ncl-step-number">{complete ? "✓" : index + 1}</span><span><strong>{title}</strong><small>{detail}</small></span></button>)}
                </div>
              </Section>
              <Section T={T}>
                <SectionHeader T={T} title="Field snapshot" />
                <div className="ncl-snapshot">
                  {[["Projected drivers",fmt0(rows.length)],["Roster spots",cfg.roster],["Field entries",fmt0(fieldEntryCount)],["Unique lineups",fmt0(fieldLineups.length)],["Simulation runs",fmt0(sims)],["Portfolio",`${fmt0(portfolioRows.length)} / ${fmt0(activeContest?.maxEntriesPerUser || 0)}`]].map(([label,value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}
                </div>
                <p className="ncl-help ncl-sidebar-help">Settings are remembered for each series and site.</p>
              </Section>
            </aside>
          </div>
        )}

        {showErrorBanner ? (
          <div
            className={`rounded border px-3 py-2 text-sm ${isDark ? "border-red-900/50 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-700"}`}
          >
            {[genError, simError, projErr, idsErr, contestsErr]
              .filter(Boolean)
              .map((e) => String(e))
              .join(" • ")}
          </div>
        ) : null}

        {notes.length > 0 ? (
          <Section T={T}>
            <SectionHeader
              T={T}
              compact
              title="Notes"
              right={
                <Btn small onClick={() => setNotesExpanded((v) => !v)}>
                  {notesExpanded ? "Hide" : "Show"}
                </Btn>
              }
            />
            {notesExpanded ? (
              <div className="px-3 py-2 text-sm">
                <ul className="space-y-1">
                  {notes.map((n, idx) => (
                    <li key={idx}>• {String(n)}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>
        ) : null}

        {workspaceTab === "field" && !fieldLineups.length ? (
          <Section T={T}><div className="ncl-empty"><div className="ncl-eyebrow">NASCAR CONTEST LAB</div><h2>Build your contest field</h2><p>Choose a contest and shape the competition before generating your first field.</p><Btn primary onClick={() => setWorkspaceTab("overview")}>Go to setup</Btn></div></Section>
        ) : workspaceTab === "field" ? (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <Section T={T} className="xl:col-span-8">
              <SectionHeader
                T={T}
                title="Generated field"
                subtitle="A unique lineup can represent multiple contest entries. Run simulation to compare results."
                right={
                  <div className="flex gap-2 flex-wrap">
                    <Btn primary onClick={simulateField} disabled={isGenerating || isSimming || !fieldLineups.length}>{isSimming ? "Running…" : "Run simulation"}</Btn>
                    <Btn small onClick={selectAllFieldLineups}>
                      Select All
                    </Btn>
                    <Btn small onClick={clearSelectedFieldLineups}>
                      Clear
                    </Btn>
                    <Btn small onClick={() => setSelectionMode("first_x")}>
                      Use First X
                    </Btn>
                    <Btn small onClick={() => setSelectionMode("all")}>
                      Use All
                    </Btn>
                    <Input
                      T={T}
                      className="w-20"
                      value={selectFirstX}
                      onChange={(e) => setSelectFirstX(e.target.value)}
                    />
                    <Btn
                      small
                      onClick={() =>
                        downloadSiteLineupsCSV({
                          lineups: fieldLineups,
                          site,
                          rosterSize: cfg.roster,
                          siteIds,
                          fname: `field_${site}_ids.csv`,
                        })
                      }
                    >
                      Export IDs
                    </Btn>
                  </div>
                }
              />

              <SpreadsheetTable T={T} maxHeight="760px">
                <thead className={T.tableHead}>
                  <tr>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2 text-center">#</th>
                    <SortableTh
                      label="Lineup"
                      sortKey="drivers"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                      align="left"
                      className="ncl-lineup-column"
                    />

                    <SortableTh
                      label="Salary"
                      sortKey="salary"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                    />
                    <SortableTh
                      label="Proj"
                      sortKey="total"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                    />
                    <SortableTh
                      label="pOWN"
                      sortKey="totalPown"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                    />
                    <SortableTh
                      label="Opt"
                      sortKey="totalOpt"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                    />
                    <SortableTh
                      label="Geo"
                      sortKey="geoMeanPown"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                    />
                    <SortableTh
                      label="Dupes"
                      sortKey="dupes"
                      sortCfg={fieldPoolSort}
                      onSort={onFieldPoolSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedFieldLineups.map((L, idx) => {
                    const active =
                      selectionMode === "all"
                        ? true
                        : selectionMode === "first_x"
                          ? fieldLineups.findIndex((x) => x._key === L._key) <
                            Math.max(0, Number(selectFirstX) || 0)
                          : (selectedFieldLineupKeys || []).includes(L._key);
                    return (
                      <DataRow
                        key={L._key}
                        index={idx}
                        selected={active}
                        onClick={() => {
                          setSelectionMode("manual");
                          setSelectedFieldLineupKeys((prev) => {
                            const set = new Set(prev || []);
                            if (set.has(L._key)) set.delete(L._key);
                            else set.add(L._key);
                            return [...set];
                          });
                        }}
                        T={T}
                      >
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox" checked={active} readOnly />
                        </td>
                        <td className="px-2 py-2 text-center">{idx + 1}</td>
                        <td className="ncl-lineup-cell text-left"><div className="ncl-driver-chips">{(L.drivers || []).map(d => <span key={d}>{driverWithQual(d, rowsByDriver)}</span>)}</div></td>

                        <td className="px-2 py-2 text-center">
                          {fmt0(L.salary)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {fmt1(L.total)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {fmt1(L.totalPown)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {fmt1(L.totalOpt)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {fmt1(L.geoMeanPown)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {fmt2(L.dupes)}
                        </td>
                        
                      </DataRow>
                    );
                  })}
                  {!sortedFieldLineups.length ? (
                    <tr>
                      <td
                        colSpan={9}
                        className={`px-3 py-8 text-center ${T.subtext}`}
                      >
                        Generate the field to populate this table.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </SpreadsheetTable>
            </Section>

            <Section T={T} className="xl:col-span-4">
              <SectionHeader
                T={T}
                title="Field Exposure"
                right={
                  <Btn
                    small
                    onClick={() =>
                      downloadExposureCSV(
                        fieldExposureRows,
                        "field_exposures.csv",
                      )
                    }
                  >
                    Export
                  </Btn>
                }
              />
              <SpreadsheetTable T={T} maxHeight="760px">
                <thead className={T.tableHead}>
                  <tr>
                    <SortableTh
                      label="Driver"
                      sortKey="driver"
                      sortCfg={fieldExposureSort}
                      onSort={onFieldExposureSort}
                      align="left"
                    />
                    <SortableTh
                      label="Count"
                      sortKey="count"
                      sortCfg={fieldExposureSort}
                      onSort={onFieldExposureSort}
                    />
                    <SortableTh
                      label="Exposure %"
                      sortKey="exposure"
                      sortCfg={fieldExposureSort}
                      onSort={onFieldExposureSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {fieldExposureRows.map((r, idx) => (
                    <DataRow key={r.driver} index={idx} T={T}>
                      <td className="px-2 py-2 text-left">{r.driver}</td>
                      <td className="px-2 py-2 text-center">{fmt0(r.count)}</td>
                      <td className="px-2 py-2 text-center">
                        {fmt1(r.exposure ?? r.pct)}
                      </td>
                    </DataRow>
                  ))}
                </tbody>
              </SpreadsheetTable>
            </Section>
          </div>
        ) : null}

        {workspaceTab === "results" && !resultLineups.length ? (
          <Section T={T}><div className="ncl-empty"><div className="ncl-eyebrow">NASCAR CONTEST LAB</div><h2>Run your first simulation</h2><p>Generate a field, then simulate it against your contest payouts.</p><Btn primary onClick={() => setWorkspaceTab("field")}>View field</Btn></div></Section>
        ) : workspaceTab === "results" ? (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <Section T={T} className="xl:col-span-4">
              <SectionHeader
                T={T}
                title="Contest results"
                subtitle="Review performance, then save selected lineups to your portfolio."
                right={
                  resultLineups.length ? (
                    <Btn
                      small
                      onClick={() => downloadSimResultsCSV(resultLineups)}
                    >
                      Export Results
                    </Btn>
                  ) : null
                }
              />
              <div className="grid grid-cols-2">
                <MetricCell
                  T={T}
                  label="Field ROI"
                  value={fmt2(resultData?.fieldSummary?.roi)}
                />
                <MetricCell
                  T={T}
                  label="Field Cash"
                  value={fmtPct(resultData?.fieldSummary?.cashRate)}
                />
                <MetricCell
                  T={T}
                  label="Field Win"
                  value={fmtPct(resultData?.fieldSummary?.winRate)}
                />
                <MetricCell
                  T={T}
                  label="Top 1"
                  value={fmtPct(resultData?.fieldSummary?.top1Rate)}
                />
                <MetricCell
                  T={T}
                  label="Avg Dupes"
                  value={fmt2(resultData?.fieldSummary?.avgDupes)}
                />
                <MetricCell
                  T={T}
                  label="Avg Finish"
                  value={fmt2(resultData?.fieldSummary?.avgFinish)}
                />
                <MetricCell
                  T={T}
                  label="Selected"
                  value={fmt0(selectedResultRows.length)}
                  sub="result rows"
                />
                <MetricCell
                  T={T}
                  label="Portfolio"
                  value={fmt0(portfolioRows.length)}
                  sub="saved rows"
                />
              </div>

              <Toolbar T={T}>
                <div className="flex flex-wrap gap-2">
                  <Btn
                    small
                    onClick={selectAllResultLineups}
                    disabled={!resultLineups.length}
                  >
                    Select All
                  </Btn>
                  <Btn
                    small
                    onClick={clearSelectedResultLineups}
                    disabled={!selectedResultRows.length}
                  >
                    Clear
                  </Btn>
                  <Btn
                    small
                    primary
                    onClick={addSelectedResultsToPortfolio}
                    disabled={!selectedResultRows.length}
                  >
                    Add Selected
                  </Btn>
                  <Btn
                    small
                    onClick={replacePortfolioFromResults}
                    disabled={!selectedResultRows.length}
                  >
                    Replace Portfolio
                  </Btn>
                </div>
              </Toolbar>
            </Section>

            <Section T={T} className="xl:col-span-8">
<SectionHeader
  T={T}
  title="Lineup Results"
  subtitle="Post-sim selection table. Build your portfolio from sorted/filtered sim results."
/>

<div
  className={`px-3 py-2 border-b ${
    isDark ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"
  }`}
>
  <div className="flex flex-col gap-2">
<div className="grid grid-cols-1 md:grid-cols-[160px_1fr_120px_90px] gap-2">
  <Select
    T={T}
    value={resultDriverMode}
    onChange={(e) => setResultDriverMode(e.target.value)}
  >
    <option value="all">All Lineups</option>
    <option value="include">Include Driver(s)</option>
    <option value="exclude">Exclude Driver(s)</option>
  </Select>

  <div className="relative">
    <Input
      T={T}
      aria-label={"result Driver Filter"}
                          value={resultDriverFilter}
      onChange={(e) => setResultDriverFilter(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && resultDriverOptions.length) {
          e.preventDefault();
          addResultDriverFilter(resultDriverOptions[0].driver);
        }
      }}
      placeholder="Search/click drivers..."
      disabled={resultDriverMode === "all"}
    />

    {resultDriverMode !== "all" && resultDriverFilter.trim() ? (
      <div
        className={`absolute z-30 mt-1 w-full rounded border shadow-lg max-h-64 overflow-auto ${
          isDark
            ? "border-slate-700 bg-slate-950"
            : "border-slate-300 bg-white"
        }`}
      >
        {resultDriverOptions.length ? (
          resultDriverOptions.map((r) => (
            <button
              key={r.driver}
              type="button"
              onClick={() => addResultDriverFilter(r.driver)}
              className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-3 ${
                isDark ? "hover:bg-slate-800" : "hover:bg-slate-100"
              }`}
            >
              <span>{driverWithQual(r.driver, rowsByDriver)}</span>
              <span className={`text-xs ${T.subtext}`}>
                {fmt1(r.proj)} proj
              </span>
            </button>
          ))
        ) : (
          <div className={`px-3 py-2 text-sm ${T.subtext}`}>
            No drivers found.
          </div>
        )}
      </div>
    ) : null}
  </div>

  <Select
    T={T}
    value={resultDriverMatch}
    onChange={(e) => setResultDriverMatch(e.target.value)}
    disabled={resultDriverMode === "all"}
  >
    <option value="any">Match Any</option>
    <option value="all">Match All</option>
  </Select>

  <Input
    T={T}
    className="text-center"
    value={resultSelectCount}
    onChange={(e) => setResultSelectCount(e.target.value)}
    title="Number of top visible lineups to select"
  />
</div>

{resultDriverMode !== "all" && selectedResultDrivers.length ? (
  <div className="flex items-center gap-2 flex-wrap">
    {(selectedResultDrivers || []).map((driver) => (
      <button
        key={driver}
        type="button"
        onClick={() => removeResultDriverFilter(driver)}
        className={`rounded-full border px-2.5 py-1 text-xs ${
          isDark
            ? "border-slate-700 bg-slate-900 hover:bg-slate-800"
            : "border-slate-300 bg-white hover:bg-slate-100"
        }`}
        title="Click to remove"
      >
        {driverWithQual(driver, rowsByDriver)} ×
      </button>
    ))}

    <Btn small onClick={clearResultDriverFilters}>
      Clear Drivers
    </Btn>
  </div>
) : null}

    <div className="flex items-center gap-2 flex-wrap">
      <Btn small onClick={selectTopXResultLineups} disabled={!resultLineups.length}>
        Select Top {resultSelectCount || 0}
      </Btn>

      <Btn small onClick={addTopXResultLineups} disabled={!resultLineups.length}>
        Add Top {resultSelectCount || 0}
      </Btn>

      <Btn small onClick={selectAllResultLineups} disabled={!resultLineups.length}>
        Select All Visible
      </Btn>

      <Btn small onClick={clearSelectedResultLineups} disabled={!selectedResultRows.length}>
        Clear
      </Btn>

      <Btn
        small
        primary
        onClick={addSelectedResultsToPortfolio}
        disabled={!selectedResultRows.length}
      >
        Add Selected
      </Btn>

      <Btn
        small
        onClick={replacePortfolioFromResults}
        disabled={!selectedResultRows.length}
      >
        Replace Portfolio
      </Btn>

      <div className={`ml-auto text-xs ${T.subtext}`}>
        Showing {fmt0(resultLineups.length)} • Selected {fmt0(selectedResultRows.length)}
      </div>
    </div>
  </div>
</div>
              <SpreadsheetTable T={T} maxHeight="680px">
                <thead className={T.tableHead}>
                  <tr>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2 text-center">#</th>
                    <SortableTh
                      label="Lineup"
                      sortKey="drivers"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                      align="left"
                      className="ncl-lineup-column"
                    />

                    <SortableTh
                      label="Source"
                      sortKey="source"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Salary"
                      sortKey="salary"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Proj"
                      sortKey="total"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="ROI"
                      sortKey="roi"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Win %"
                      sortKey="winPct"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Top 1 %"
                      sortKey="top1Pct"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Top 10 %"
                      sortKey="top10Pct"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Cash %"
                      sortKey="cashPct"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Avg Finish"
                      sortKey="avgFinish"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Avg Dupes"
                      sortKey="dupesAvg"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                    <SortableTh
                      label="Avg Payout"
                      sortKey="payoutsAvg"
                      sortCfg={resultsSort}
                      onSort={onResultsSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {resultLineups.map((r, idx) => {
                    const active = (selectedResultKeys || []).includes(r.key);
                    return (
                      <DataRow
                        key={r.key}
                        index={idx}
                        selected={active}
                        onClick={() => {
                          setSelectedResultKeys((prev) => {
                            const set = new Set(prev || []);
                            if (set.has(r.key)) set.delete(r.key);
                            else set.add(r.key);
                            return [...set];
                          });
                        }}
                        T={T}
                      >
                        <td className="px-2 py-1 text-center align-middle">
                          <input type="checkbox" checked={active} readOnly />
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {idx + 1}
                        </td>

                        <td className="ncl-lineup-cell text-left align-middle">
                          <div className="ncl-driver-chips">
                            {(r.drivers || []).map((d) => (
                              <span key={d} className="whitespace-nowrap">
                                {driverWithQual(d, rowsByDriver)}
                              </span>
                            ))}
                          </div>
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {r.source || "field"}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt0(r.salary)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt1(r.total)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt2(r.roi)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt1(r.winPct)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt1(r.top1Pct)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt1(r.top10Pct)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt1(r.cashPct)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt2(r.avgFinish)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt2(r.dupesAvg)}
                        </td>

                        <td className="px-2 py-1 text-center align-middle">
                          {fmt2(r.payoutsAvg)}
                        </td>

                                                
                      </DataRow>
                    );
                  })}
                  {!resultLineups.length ? (
                    <tr>
                      <td
                        colSpan={14}
                        className={`px-3 py-8 text-center ${T.subtext}`}
                      >
                        No simulation results yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </SpreadsheetTable>
            </Section>
          </div>
        ) : null}

        {workspaceTab === "portfolio" && !portfolioRows.length ? (
          <Section T={T}><div className="ncl-empty"><div className="ncl-eyebrow">NASCAR CONTEST LAB</div><h2>Your portfolio starts here</h2><p>Select evaluated lineups in Results, then save them here to review exposures and export.</p><Btn primary onClick={() => setWorkspaceTab("results")}>View results</Btn></div></Section>
        ) : workspaceTab === "portfolio" ? (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <Section T={T} className="xl:col-span-8">
              <SectionHeader
                T={T}
                title="Your portfolio"
                subtitle="Review saved lineups, compare exposures, and export entries for your site."
                right={
                  <div className="flex gap-2 flex-wrap">
                    <Btn
                      small
                      onClick={clearPortfolio}
                      disabled={!portfolioRows.length}
                    >
                      Clear
                    </Btn>
                    <Btn
                      small
                      onClick={() =>
                        downloadSiteLineupsCSV({
                          lineups: portfolioRows,
                          site,
                          rosterSize: cfg.roster,
                          siteIds,
                          fname: `portfolio_${site}_ids.csv`,
                        })
                      }
                      disabled={!portfolioRows.length}
                    >
                      Export IDs
                    </Btn>
                    <Btn
                      small
                      onClick={() =>
                        downloadExposureCSV(
                          portfolioExposureRows,
                          "portfolio_exposures.csv",
                        )
                      }
                      disabled={!portfolioRows.length}
                    >
                      Export Exposures
                    </Btn>
                  </div>
                }
              />

              <div className="grid grid-cols-2 md:grid-cols-6 border-b border-slate-200 dark:border-slate-800">
                <MetricCell
                  T={T}
                  label="Lineups"
                  value={fmt0(portfolioSummary.count)}
                />
                <MetricCell
                  T={T}
                  label="Avg Salary"
                  value={fmt0(portfolioSummary.avgSalary)}
                />
                <MetricCell
                  T={T}
                  label="Avg Proj"
                  value={fmt1(portfolioSummary.avgProj)}
                />
                <MetricCell
                  T={T}
                  label="Avg pOWN"
                  value={fmt1(portfolioSummary.avgPown)}
                />
                <MetricCell
                  T={T}
                  label="Avg ROI"
                  value={fmt2(portfolioSummary.avgRoi)}
                />
                <MetricCell
                  T={T}
                  label="Cash %"
                  value={fmtPct(portfolioSummary.cashPct)}
                />
              </div>

              <SpreadsheetTable T={T} maxHeight="760px">
                <thead className={T.tableHead}>
                  <tr>
                    <th className="px-2 py-2 text-center">#</th>
                    <SortableTh
                      label="Lineup"
                      sortKey="drivers"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                      align="left"
                      className="ncl-lineup-column"
                    />

                    <SortableTh
                      label="Salary"
                      sortKey="salary"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <SortableTh
                      label="Proj"
                      sortKey="total"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <SortableTh
                      label="ROI"
                      sortKey="roi"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <SortableTh
                      label="Win %"
                      sortKey="winPct"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <SortableTh
                      label="Top 10 %"
                      sortKey="top10Pct"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <SortableTh
                      label="Cash %"
                      sortKey="cashPct"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <SortableTh
                      label="Avg Finish"
                      sortKey="avgFinish"
                      sortCfg={portfolioSort}
                      onSort={onPortfolioSort}
                    />
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>

                <tbody>
                  {sortedPortfolioRows.map((L, idx) => (
                    <DataRow key={L._portfolioKey} index={idx} T={T}>
                      <td className="px-2 py-1 text-center align-middle">
                        {idx + 1}
                      </td>

                      <td className="ncl-lineup-cell text-left align-middle">
                        <div className="ncl-driver-chips">
                          {(L.drivers || []).map((d) => (
                            <span key={d} className="whitespace-nowrap">
                              {driverWithQual(d, rowsByDriver)}
                            </span>
                          ))}
                        </div>
                      </td>

                        <td className="px-2 py-1 text-center align-middle">
                        {fmt0(L.salary)}
                      </td>

                      <td className="px-2 py-1 text-center align-middle">
                        {fmt1(L.total)}
                      </td>

                      <td className="px-2 py-1 text-center align-middle">
                        {fmt2(L.roi)}
                      </td>

                      <td className="px-2 py-1 text-center align-middle">
                        {fmt1(L.winPct)}
                      </td>

                      <td className="px-2 py-1 text-center align-middle">
                        {fmt1(L.top10Pct)}
                      </td>

                      <td className="px-2 py-1 text-center align-middle">
                        {fmt1(L.cashPct)}
                      </td>

                      <td className="px-2 py-1 text-center align-middle">
                        {fmt2(L.avgFinish)}
                      </td>

                      

                      <td className="px-2 py-1 text-center align-middle">
                        <Btn small onClick={() => removePortfolioLineup(idx)}>
                          Remove
                        </Btn>
                      </td>
                    </DataRow>
                  ))}
                  {!sortedPortfolioRows.length ? (
                    <tr>
                      <td
                        colSpan={10}
                        className={`px-3 py-8 text-center ${T.subtext}`}
                      >
                        Select simmed results, then add them to portfolio.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </SpreadsheetTable>
            </Section>

            <div className="xl:col-span-4 space-y-4">
              <Section T={T}>
                <SectionHeader
                  T={T}
                  title="Portfolio Exposure"
                  subtitle="Click a driver for pairings."
                />
                <SpreadsheetTable T={T} maxHeight="320px">
                  <thead className={T.tableHead}>
                    <tr>
                      <SortableTh
                        label="Driver"
                        sortKey="driver"
                        sortCfg={portfolioExposureSort}
                        onSort={onPortfolioExposureSort}
                        align="left"
                      />
                      <SortableTh
                        label="Count"
                        sortKey="count"
                        sortCfg={portfolioExposureSort}
                        onSort={onPortfolioExposureSort}
                      />
                      <SortableTh
                        label="Exposure %"
                        sortKey="exposure"
                        sortCfg={portfolioExposureSort}
                        onSort={onPortfolioExposureSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioExposureRows.map((r, idx) => {
                      const active = selectedExposureDriver === r.driver;
                      return (
                        <DataRow
                          key={r.driver}
                          index={idx}
                          selected={active}
                          onClick={() =>
                            setSelectedExposureDriver((prev) =>
                              prev === r.driver ? null : r.driver,
                            )
                          }
                          T={T}
                        >
                          <td className="px-2 py-2 text-left">
                            {driverWithQual(r.driver, rowsByDriver)}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {fmt0(r.count)}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {fmt1(r.exposure)}
                          </td>
                        </DataRow>
                      );
                    })}
                  </tbody>
                </SpreadsheetTable>
              </Section>

              <Section T={T}>
                <SectionHeader
                  T={T}
                  title="Driver Pairings"
                  subtitle={
                    selectedExposureDriver
                      ? selectedExposureDriver
                      : "Pick a driver from exposure."
                  }
                  right={
                    selectedExposureDriver ? (
                      <Btn
                        small
                        onClick={() => setSelectedExposureDriver(null)}
                      >
                        Clear
                      </Btn>
                    ) : null
                  }
                />
                <SpreadsheetTable T={T} maxHeight="320px">
                  <thead className={T.tableHead}>
                    <tr>
                      <SortableTh
                        label="Paired Driver"
                        sortKey="driver"
                        sortCfg={pairingSort}
                        onSort={onPairingSort}
                        align="left"
                      />
                      <SortableTh
                        label="Count"
                        sortKey="count"
                        sortCfg={pairingSort}
                        onSort={onPairingSort}
                      />
                      <SortableTh
                        label="Pair %"
                        sortKey="pct"
                        sortCfg={pairingSort}
                        onSort={onPairingSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDriverPairings.map((r, idx) => (
                      <DataRow key={r.driver} index={idx} T={T}>
                        <td className="px-2 py-2 text-left">
                          {driverWithQual(r.driver, rowsByDriver)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {fmt0(r.count)}
                        </td>
                        <td className="px-2 py-2 text-center">{fmt1(r.pct)}</td>
                      </DataRow>
                    ))}
                    {!selectedDriverPairings.length ? (
                      <tr>
                        <td
                          colSpan={3}
                          className={`px-3 py-8 text-center ${T.subtext}`}
                        >
                          No pairing data yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </SpreadsheetTable>
              </Section>
            </div>
          </div>
        ) : null}

        {workspaceTab === "drivers" ? (
          <Section T={T}>
            <SectionHeader
              T={T}
              title="Driver Stats"
              subtitle="Driver-level view across simmed lineups and your saved portfolio."
              right={
                <div className="flex items-center gap-2">
                  <Input
                    T={T}
                    className="w-64"
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                    placeholder="Search driver"
                  />
                  <Btn
                    small
                    onClick={() =>
                      downloadDriverStatsCSV(
                        driverStatsRows,
                        "driver_stats.csv",
                      )
                    }
                    disabled={!driverStatsRows.length}
                  >
                    Export
                  </Btn>
                </div>
              }
            />
            <SpreadsheetTable T={T} maxHeight="760px">
              <thead className={T.tableHead}>
                <tr>
                  <SortableTh
                    label="Driver"
                    sortKey="driver"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                    align="left"
                  />
                  <SortableTh
                    label="Qual"
                    sortKey="qual"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Salary"
                    sortKey="salary"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Proj"
                    sortKey="proj"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Proj pOWN"
                    sortKey="pown"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Opt %"
                    sortKey="opt"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Field Own"
                    sortKey="fieldOwn"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Port Own"
                    sortKey="portOwn"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Leverage"
                    sortKey="leverage"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Avg ROI"
                    sortKey="avgRoi"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Cash %"
                    sortKey="cashPct"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Win %"
                    sortKey="winPct"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Top 10 %"
                    sortKey="top10Pct"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                  <SortableTh
                    label="Lineups"
                    sortKey="lineups"
                    sortCfg={driverSort}
                    onSort={onDriverSort}
                  />
                </tr>
              </thead>
              <tbody>
                {driverStatsRows.map((r, idx) => (
                  <DataRow key={r.driver} index={idx} T={T}>
                    <td className="px-2 py-2 text-left">{r.driver}</td>
                    <td className="px-2 py-2 text-center">{r.qual || "—"}</td>
                    <td className="px-2 py-2 text-center">{fmt0(r.salary)}</td>
                    <td className="px-2 py-2 text-center">{fmt1(r.proj)}</td>
                    <td className="px-2 py-2 text-center">{fmt1(r.pown)}</td>
                    <td className="px-2 py-2 text-center">{fmt1(r.opt)}</td>
                    <td className="px-2 py-2 text-center">
                      {fmt1(r.fieldOwn)}
                    </td>
                    <td className="px-2 py-2 text-center">{fmt1(r.portOwn)}</td>
                    <td
                      className={`px-2 py-2 text-center ${r.leverage > 0 ? "text-green-500" : r.leverage < 0 ? "text-red-500" : ""}`}
                    >
                      {fmt1(r.leverage)}
                    </td>
                    <td className="px-2 py-2 text-center">{fmt2(r.avgRoi)}</td>
                    <td className="px-2 py-2 text-center">{fmt1(r.cashPct)}</td>
                    <td className="px-2 py-2 text-center">{fmt1(r.winPct)}</td>
                    <td className="px-2 py-2 text-center">
                      {fmt1(r.top10Pct)}
                    </td>
                    <td className="px-2 py-2 text-center">{fmt0(r.lineups)}</td>
                  </DataRow>
                ))}
                {!driverStatsRows.length ? (
                  <tr>
                    <td
                      colSpan={14}
                      className={`px-3 py-8 text-center ${T.subtext}`}
                    >
                      Sim the field to populate driver stats.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </SpreadsheetTable>
          </Section>
        ) : null}

        {workspaceTab === "reference" ? (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <Section T={T} className="xl:col-span-7">
              <SectionHeader
                T={T}
                title="Player Pool"
                right={
                  <Input
                    T={T}
                    className="w-72"
                    value={playerPoolSearch}
                    onChange={(e) => setPlayerPoolSearch(e.target.value)}
                    placeholder="Search driver, qual, salary"
                  />
                }
              />
              <SpreadsheetTable T={T} maxHeight="760px">
                <thead className={T.tableHead}>
                  <tr>
                    <SortableTh
                      label="Driver"
                      sortKey="driver"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                      align="left"
                    />
                    <SortableTh
                      label="Qual"
                      sortKey="qual"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="Salary"
                      sortKey="salary"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="Proj"
                      sortKey="proj"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="Val"
                      sortKey="val"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="Floor"
                      sortKey="floor"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="Ceiling"
                      sortKey="ceil"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="pOWN %"
                      sortKey="pown"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                    <SortableTh
                      label="Opt %"
                      sortKey="opt"
                      sortCfg={playerPoolSort}
                      onSort={onPlayerPoolSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {playerPoolRows.map((r, idx) => (
                    <DataRow key={r.driver} index={idx} T={T}>
                      <td className="px-2 py-2 text-left">{r.driver}</td>
                      <td className="px-2 py-2 text-center">{r.qual || "—"}</td>
                      <td className="px-2 py-2 text-center">
                        {fmt0(r.salary)}
                      </td>
                      <td className="px-2 py-2 text-center">{fmt1(r.proj)}</td>
                      <td className="px-2 py-2 text-center">{fmt2(r.val)}</td>
                      <td className="px-2 py-2 text-center">{fmt1(r.floor)}</td>
                      <td className="px-2 py-2 text-center">{fmt1(r.ceil)}</td>
                      <td className="px-2 py-2 text-center">
                        {fmt1((r.pown || 0) * 100)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {fmt1((r.opt || 0) * 100)}
                      </td>
                    </DataRow>
                  ))}
                </tbody>
              </SpreadsheetTable>
            </Section>

            <Section T={T} className="xl:col-span-5">
              <SectionHeader
                T={T}
                title="Contest Payout Structure"
                subtitle={
                  (effectiveContestBucket === "custom"
                    ? makeCustomContest()
                    : selectedContest
                  )?.name || "No contest selected"
                }
              />
              <SpreadsheetTable T={T} maxHeight="760px">
                <thead className={T.tableHead}>
                  <tr>
                    <SortableTh
                      label="Tier"
                      sortKey="tierIndex"
                      sortCfg={payoutSort}
                      onSort={onPayoutSort}
                    />
                    <SortableTh
                      label="Min Pos"
                      sortKey="minPosition"
                      sortCfg={payoutSort}
                      onSort={onPayoutSort}
                    />
                    <SortableTh
                      label="Max Pos"
                      sortKey="maxPosition"
                      sortCfg={payoutSort}
                      onSort={onPayoutSort}
                    />
                    <SortableTh
                      label="Cash"
                      sortKey="cashValue"
                      sortCfg={payoutSort}
                      onSort={onPayoutSort}
                    />
                    <SortableTh
                      label="Type"
                      sortKey="payoutDescriptionType"
                      sortCfg={payoutSort}
                      onSort={onPayoutSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedPayouts.map((p, idx) => (
                    <DataRow
                      key={`${p.tierIndex}-${p.minPosition}-${p.maxPosition}`}
                      index={idx}
                      T={T}
                    >
                      <td className="px-2 py-2 text-center">
                        {fmt0(p.tierIndex)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {fmt0(p.minPosition)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {fmt0(p.maxPosition)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {p.cashText || fmtMoney(p.cashValue)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {p.payoutDescriptionType || "Text"}
                      </td>
                    </DataRow>
                  ))}
                  {!sortedPayouts.length ? (
                    <tr>
                      <td
                        colSpan={5}
                        className={`px-3 py-8 text-center ${T.subtext}`}
                      >
                        Select a contest to see payout tiers.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </SpreadsheetTable>
            </Section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
